// i18n area 'cluster' (TVirt task #127 layout, adopted for TVStor M1): section
// cluster — the node registry. M2d (2026-08-03, datapool/vdisk model; vdisk
// nomenclature, dettatura 2026-08-03, 2° giro): the disk eligibility verb
// becomes MARK (mark-disk, dettatura) and the node detail grows the Vdisks
// tab. DISK-1 (2026-08-05 — design ratificato per intero da Luca 2026-08-04,
// docs/vdisk-partial-disk-2026-08-04.md): the whole-disk md model is RETIRED
// pre-GA; the node detail speaks the chunk stack — L2 per-disk chunk regimes
// (create-chunks, anonymous fungible chunks, counters), L3 raids over
// per-disk aggregates (new tab), L4 vdisks wrapping a raid or jbod chunks of
// ONE disk (the old type/members dialog keys died with the surface).

export const en = {
  cluster: {
    nodes: {
      title: 'Nodes',
      name: 'Name',

      // #204 CEPH-STRETCH Fase A (2026-08-24): the multi-site topology label.
      site: 'Site',
      phase: 'Phase',
      phases: {
        'pending-join': 'Pending join',
        joining: 'Joining',

        // JOIN-FANTASMA: the two-phase flip — the handshake done, the row
        // waits for the joiner's own confirm before becoming a member.
        joinPending: 'Awaiting confirmation',
        member: 'Member'
      },
      storeMember: 'Cluster store',
      replicatedGeneration: 'Replicated generation',
      services: 'Services',

      // ENC-1 (2026-08-05): the replica-tunnel glance column.
      tunnel: 'Replica tunnel',
      tunnelUp: 'Up',
      tunnelPeersDown: '{n} peer(s) down',
      lastSeen: 'Last seen',
      neverSeen: 'Never seen',

      // ENEA-A-R16 «drain» (2026-10-24): the maintenance leaf — state chip,
      // the two verbs (confirm-typed, sudo), what was ceded, what is pending.
      maintenance: {
        column: 'Maintenance',
        tabTitle: 'Maintenance',
        states: {
          draining: 'Draining',
          maintenance: 'In maintenance',
          undraining: 'Undraining',
          inService: 'In service'
        },
        state: 'State',
        since: 'Since',
        hours: '{h} h',
        requestedBy: 'Requested by',
        reason: 'Reason',
        task: 'Task',
        taskLink: 'Open the task',
        enteredAt: 'Entered maintenance',
        nonDestructive:
          'The drain is never destructive: no OSD is removed, no data is moved — only roles and addresses change hands (floating gateway addresses after a RADOS grace, multiscsi volume authority to the peer, control roles to the next member; `noout` for the duration).',
        drainAction: 'Drain (maintenance)',
        undrainAction: 'Undrain (back in service)',
        drainTitle: 'Drain {name} for maintenance?',
        undrainTitle: 'Bring {name} back in service?',
        drainScale:
          'The node cedes its floating gateway addresses (a RADOS grace first), its multiscsi volume authority (the peer takes over) and its control roles, sets `noout` on its Ceph clusters and stops its consolidated gateways once the addresses are gone. Nothing is destroyed. Admission gates: MON quorum, a healthy peer for every served volume, the store role.',
        undrainScale:
          '`noout` is unset; the beats claim again — the addresses come home per the gateway failover policy (automatic after the delay, or never with manual); multiscsi volumes STAY on the peer (use the failover verbs to move them back).',
        confirmLabel: 'Type the node name to confirm',
        confirmHint: 'Exactly {name}',
        reasonLabel: 'Reason (optional)',
        reasonHint: 'Kept on the maintenance leaf — the reboot, the firmware, the ticket',
        drainConfirm: 'Drain',
        undrainConfirm: 'Undrain',
        started: 'Task {task} started — the node detail shows what is ceded and what is still pending.',
        startedNoTask: 'Accepted — the node detail shows what is ceded and what is still pending.',
        ceded: {
          title: 'Ceded',
          addresses: 'Gateway addresses',
          volumes: 'Volumes (multiscsi authority)',
          roles: 'Control roles',
          noout: 'OSD noout',
          nooutOn: 'set',
          nooutOff: 'not set',
          none: 'Nothing recorded yet'
        },
        pending: {
          title: 'Still held',
          addresses: 'Addresses',
          volumes: 'Volumes',
          roles: 'Roles',
          settled: 'Nothing is held any more — the node has ceded everything.',
          unknown: 'Not reported by this backend'
        },
        roles: {
          control: 'control node',
          catalog: 'catalog',
          mirror: 'cephfs-mirror',
          forwarder: 'forwarder'
        },
        software: {
          title: 'Software',
          version: 'Version',
          startedAt: 'Worker started',
          unknown: 'Not reported yet (a worker that predates this leaf)',
          returned: 'Returned after the maintenance entry',
          notReturned: 'Not restarted since the maintenance entry'
        },
        inServiceHint: 'The node is in service: no maintenance leaf.'
      },

      // M2a data-disk surface, verb renamed to mark-disk in M2d (2026-08-03):
      // the observed block-device inventory with the per-row mark/unmark
      // toggle (dataDisks, explicit eligibility — serial pinned at marking).
      tabOverview: 'Overview',
      tabDisks: 'Disks (observed)',

      // PKI-2 (#181 ondata 4): the certificate verbs — semantics mirrored
      // from the CLI (revoke-node-certs / reinstate-node-certs).
      certs: {
        revokeAction: "Revoke the node's cluster certificates",
        revokeTitle: 'Revoke the certificates of {name}?',
        revokeScale:
          "Every outstanding leaf certificate issued to this node goes on the CA-signed revocation list, and the node is marked so it is issued nothing further. Both halves matter: the list withdraws what was already issued — every node verifies it OFFLINE during the TLS handshake — and the mark stops the evicted node's worker from simply obtaining a fresh serial within the minute.",
        revokeWindow:
          'The declared cost: each peer applies the list IT HAS — the revocation reaches a peer within one CRL refresh period (plus any time it cannot reach the store). The node.crlAgeSeconds metric shows who is still behind. Idempotent: pressing twice is safe.',
        revokeConfirm: 'Revoke certificates',
        revokeDone:
          '{count} certificate(s) revoked; the revocation list is now generation {crl}. Each node reports this number as it picks the list up — watch node.crlAgeSeconds for who is still behind.',
        revokeZeroHint:
          '0 means the node had none left to revoke — an already-revoked node, or one that never obtained a leaf. The mark is set either way.',
        reinstateAction: 'Let the node obtain certificates again',
        reinstateTitle: 'Reinstate the certificates of {name}?',
        reinstateScale:
          'Clears the revocation mark: the node obtains a FRESH certificate on its next PKI pass. Nothing comes off the revocation list, deliberately — revoked serials stay revoked until natural expiry: readmitting the NODE does not make a stolen CERTIFICATE trustworthy again.',
        reinstateConfirm: 'Reinstate',
        reinstateDone:
          'Mark cleared — the node obtains a fresh certificate on its next PKI pass (the previously revoked ones stay revoked).'
      },

      // #181 ondata 5 — the node lifecycle in console: pre-registration with
      // the write-once JoinToken (contract cluster.yaml: «rendered ONCE,
      // here» — no read path re-renders it) and the joining-only revoke
      // (SCOPE FENCE 2026-08-04: NOT node removal).
      join: {
        title: 'Pre-register a node',
        hint: "Mints the single-use join token the new node presents during its handshake. The name MUST equal the name the node's installation will choose; the token lives 30 minutes, is stored hashed server-side, and is RENDERED ONCE in this dialog — losing it means revoke + re-register. The registration itself does not expire with its token: an expired-token row stays visible (honest registry) until revoked or joined.",
        name: 'Node name',
        nameHint: "Must equal the name the new node's installation will choose",
        namePattern: 'Lowercase letters, digits and dashes; letter or digit at the edges (max 63)',
        address: 'Management address',
        addressHint: 'The address the cluster dials for the join handshake',
        confirm: 'Mint the join token',
        shownOnce:
          'This token is shown ONCE — it exists only in this response. Verify the CA fingerprint out-of-band on the new node before trusting the handshake. If you lose the token, revoke the registration and re-register: tokens are never re-issued for a live registration.',
        token: 'Join token',
        caFingerprint: 'Cluster CA fingerprint',
        caHint: 'Also embedded in the token — rendered separately for operator display and scripting',
        expires: 'Expires {time} (mint time + 30 minutes, single use)',
        revokeAction: 'Revoke this registration and its token',
        revokeTitle: 'Revoke the registration of {name}?',
        revokeScale:
          'The joining row and its outstanding join token die in the same config write — a revoked token fails the handshake even inside its TTL. This is NOT node removal: only joining registrations are deletable (draining a real member is a later round with its own task chain).',
        revokeConfirm: 'Revoke registration'
      },
      disks: {
        device: 'Device',
        serial: 'Serial',

        // JBOD-DC-1 (2026-09-06): identity by WWID, dm-multipath paths, the
        // nodes that see the same disk (shared JBOD, two controllers).
        wwid: 'WWID',
        noWwid: 'none exposed',

        // ENEA-A-R11 (2026-10-23, §5): the firmware's wear counter from SMART.
        wear: 'Wear',
        wearHint:
          'The drive firmware’s wear counter (percentage used) read by smartctl every 15 minutes — above 80 % warning, above 90 % critical: plan the replacement',
        wearUnread: 'Not read yet',
        wearUnavailable: 'n/a',
        wearNoCounter: 'no counter',
        wearNoCounterHint: 'SMART answered but this drive exposes no wear counter (typical of rotational disks)',
        mediaErrors: 'media errors',
        paths: 'Paths',
        pathActive: 'up',
        pathFailed: 'DOWN',
        pathUnknown: '?',
        seenBy: 'Seen by',
        shared: 'SHARED (not usable on one node until JBOD-DC phase 2)',
        size: 'Size',
        model: 'Model',
        state: 'State',
        observedAt: 'Observed at',
        marked: 'Marked usable',
        inUse: 'In use',
        free: 'Free',
        noSerial: 'No serial exposed — cannot be marked',
        empty: 'No block devices observed yet',
        notObserved: 'Marked but absent from the observed inventory',

        // Node-detail observation re-cut (dettatura Luca 2026-08-06): the
        // detail tab is a plain read of the discovered devices — management
        // lives in the dedicated Storage pages, linked from the tab header.
        observedHint:
          'Read-only observation: what the sweep found on this node — marked-usable flag and chunk census included. Managing disks (mark, chunks, raids) and vdisks lives in the Storage pages.',
        manage: 'Manage disks →',
        manageVdisks: 'Manage vdisks →',
        hint: 'Marking is configuration — nothing touches the device until Create chunks carves it. A carved disk becomes a PV in its own volume group (one VG per disk: a disk dies alone), split into anonymous chunks of one fixed per-disk size; raids and jbod vdisks consume them by quantity.',

        // DISK-1 L2 (2026-08-05): the per-disk chunk regime — the census is a
        // COUNTER (chunks are anonymous and fungible, ratifica finale
        // 2026-08-04), rendered as the allocation bar.
        chunks: 'Chunks',
        notChunked: 'Not chunked',
        chunkRegime: '{total} × {size} GiB',
        chunkRegimeUnknown: '? × {size} GiB — total not yet confirmed',
        chunkBar: '{allocated} allocated · {free} free',
        chunkPhases: {
          pending: 'Pending',
          creating: 'Carving',
          ready: 'Ready',
          error: 'Error',
          destroying: 'Dismantling'
        },
        createChunks: 'Create chunks',
        chunkSizeGib: 'Chunk size (GiB)',
        dechunk: 'Dismantle chunks',
        dechunkTitle: 'Dismantle the chunk regime of {serial}?',
        dechunkWarning:
          "The disk's EMPTIED chunk layer is removed (vgremove/pvremove) and the disk returns to plain marked-usable state — re-chunkable at a new size. Allocated chunks refuse this: destroy the consuming raids and vdisks first.",
        dechunkConfirmLabel: 'Disk serial',
        dechunkConfirmHint: 'Type {serial} to confirm the dismantle',
        dechunkAllocatedBlock: 'Chunks are still allocated — destroy the consuming raids/vdisks first'
      },

      // DISK-1 L3 (2026-08-05): node-local md arrays over PER-DISK chunk
      // aggregates — each member takes K free chunks of ONE disk,
      // concatenated into one LV; md sees one device per disk.
      raids: {
        hint: 'A raid member is a per-disk AGGREGATE: K free chunks of one disk concatenated into one LV — md sees one device per disk. Honesty (the SHR lesson): arrays sharing disks via chunks contend under rebuild, and a raid0 loses the whole device with ANY one member disk.',
        create: 'Create raid',
        makeTitle: 'Create a raid on {name}',
        name: 'Name',
        nameHint: 'Lowercase letters, digits and dashes — unique per node',
        level: 'Level',
        levelHint: '{level} needs at least {min} member disks',
        members: 'Member aggregates',
        membersHint:
          'Give each member disk how many of its free chunks to aggregate — 0 leaves it out. Redundant levels need the SAME aggregate size in bytes on every disk (each disk counts its own chunk size); raid0 admits unequal aggregates.',
        memberFree: '{free} free × {size} GiB chunks',
        memberAggregate: 'aggregate {gib} GiB',
        noDisks: 'Fewer than two chunked disks with free chunks — carve chunks in the Data disks tab first.',
        problems: {
          'min-disks': '{level} needs at least {min} member disks — {got} selected',
          'over-free': 'Only {free} chunks free on {disk}',
          'unequal-aggregates': 'Redundant levels need the same aggregate size on every disk: {detail}'
        },
        empty: 'No raids yet',
        phase: 'Phase',
        phases: {
          pending: 'Pending',
          creating: 'Creating',
          ready: 'Ready',
          degraded: 'Degraded',
          error: 'Error',
          destroying: 'Destroying'
        },
        vdisk: 'Vdisk',
        claimed: 'Claimed',
        unclaimed: 'Unclaimed',
        mdState: 'Array state',
        size: 'Size',
        observedAt: 'Observed at',
        notObserved: 'Not yet observed',
        destroy: 'Destroy',
        destroyTitle: 'Destroy raid {name}?',
        destroyWarning:
          'The md array is stopped, its superblocks wiped and the aggregate LVs removed — the chunks return free to their disks. This cannot be undone.',
        destroyConfirmLabel: 'Raid name',
        destroyConfirmHint: 'Type {name} to confirm the destruction',
        claimedBlock:
          'Claimed by a vdisk — destroy the vdisk first (that only releases the raid; this destroy dismantles it)',
        destroyingBlock: 'Destruction already running'
      },

      // Vdisk surface (M2d 2026-08-03; REDESIGNED by DISK-1, 2026-08-05):
      // level four of the chunk stack — a vdisk wraps a raid or concatenates
      // jbod chunks of ONE disk; the old type/members keys died with the
      // whole-disk md surface (pre-GA break, ratified 2026-08-04).
      vdisks: {
        create: 'Create vdisk',
        makeTitle: 'Create a vdisk on {name}',
        name: 'Name',
        nameHint: 'Lowercase letters, digits and dashes — unique per node',
        source: 'Source',
        sourceHint: 'A vdisk wraps a raid, or concatenates chunks of ONE disk (jbod) — chosen now, immutable',
        forms: {
          raid: 'RAID — wrap a raid',
          jbod: 'JBOD — chunks of one disk',
          external: 'EXTERNAL — a LUN of a foreign SAN'
        },
        extTransport: 'Transport',
        extTransportHint: 'iSCSI (dm-multipath across the portals) or NVMe-oF (native ANA multipath)',
        extPortals: 'Portals',
        extPortalsHint:
          'host[:port], comma-separated — more portals = more paths to the SAME LUN (default port 3260 iSCSI / 4420 NVMe-oF)',
        extIqn: 'Target IQN',
        extIqnHint: 'The target on the SAN side that offers the LUN',
        extLun: 'LUN',
        extLunHint: 'LUN number (empty = 0)',
        extNqn: 'Subsystem NQN',
        extNqnHint: 'The NVMe subsystem on the SAN side',
        extNsid: 'NSID',
        extNsidHint: 'Namespace id (empty = the first namespace)',
        extCredential: 'CHAP credential',
        extCredentialHint: 'Initiator-side CHAP — an existing iSCSI CHAP credential from the keystore',
        extCredentialNone:
          'No iSCSI CHAP credential in the keystore (create one under System → Credentials, or log in without CHAP)',
        extNoCredential: 'None — no CHAP',
        extWwid: 'WWID',
        extWwidHint: 'The LUN identity when you know it (empty: observed at first login and backfilled)',
        extWwidUnobserved: 'Not observed yet',
        extHonesty:
          "Disk-fault tolerance of an external vdisk is the foreign SAN's own; TVStor adds node/site redundancy with the existing models on top (mirror volumes across vdisks). At-rest LUKS2 is not available on this form.",
        extPaths: 'Paths',
        extPathsEmpty: 'Looked — the SAN offers no path (declared loss)',
        extPathsUnknown: 'Path census not taken — could not look (multipathd/nvme-cli mute or login not established)',
        raid: 'Raid',
        raidHint: "A ready, unclaimed raid of this node — its md device becomes the vdisk's device",
        noRaids: 'No free ready raid on this node',
        disk: 'Disk',
        diskHint: 'A chunked disk with free chunks — a jbod vdisk never crosses disks',
        chunksCount: 'Chunks',
        chunksCountHint: '{free} free × {size} GiB chunks on {disk}',
        noSources: 'Nothing to build on — carve chunks in the Data disks tab (and optionally create a raid) first.',
        claimed: 'Claimed',
        empty: 'No vdisks yet',

        // VOL-CRYPT-CLUSTER (2026-09-04): the create-vdisk checkbox died — the
        // at-rest encryption is the storage cluster's, applied at the join;
        // here only the OBSERVED state stays.
        encryption: 'Encryption at rest',
        encryptedChip: 'Encrypted',
        lockedChip: 'LOCKED',
        lockedReason: 'The key could not be resolved from the keystore — the vdisk does not serve',
        openChip: 'Unlocked',
        phase: 'Phase',
        phases: {
          pending: 'Pending',
          creating: 'Creating',
          ready: 'Ready',
          degraded: 'Degraded',
          error: 'Error',
          destroying: 'Destroying'
        },
        datapool: 'Datapool',
        unclaimed: 'Unclaimed',
        deviceState: 'Device state',
        size: 'Size',
        observedAt: 'Observed at',
        notObserved: 'Not yet observed',
        destroy: 'Destroy',
        destroyTitle: 'Destroy vdisk {name}?',
        destroyWarning:
          "The vdisk's device is retired: an encrypted mapping is closed and its key reaped. A raid-backed vdisk only RELEASES its raid (the array survives, re-claimable — dismantling it is the raid's own destroy); a jbod vdisk's LV is removed and its chunks return free. This cannot be undone.",
        destroyConfirmLabel: 'Vdisk name',
        destroyConfirmHint: 'Type {name} to confirm the destruction',
        claimedBlock: 'Claimed by a datapool — destroy the datapool first',
        destroyingBlock: 'Destruction already running'
      },

      // M2e (dettatura Luca 2026-08-03, 5° giro — cascata di fencing +
      // disarm-then-kill): the node's ORDERED fencingDevices editor, guarded
      // by cluster.node.fencing AND sudo (field-level guard, cluster.yaml).
      fencing: {
        tabTitle: 'Fencing',
        hint: 'The ORDERED cascade OTHER nodes use to fence THIS node: the first level with a VERIFIED outcome wins; later levels fire only when the previous is unreachable or unverifiable. Misdeclared fencing shoots the wrong machine — saving requires sudo.',
        empty:
          "No fencing device declared — taking this node's volumes automatically relies on the witness alone, or stays manual.",
        kind: 'Kind',
        kinds: {
          bmc: 'BMC',
          switch: 'Switch',
          pdu: 'PDU'
        },
        endpoint: 'Endpoint',
        endpointHint: 'Management address — Redfish base URL for a BMC',

        // M2g (2026-08-03, the keystore round): the real device account — a
        // bmcAccount keystore credential replaces the M2e placeholder seam.
        credential: 'Device account (keystore)',
        credentialNone: 'None — mock/rig actuator only',
        credentialHint:
          'A bmcAccount keystore credential, resolved at use time — reads, kill AND self-disarm: its username is the account whose role gets degraded',
        credentialMissing:
          'Neither a keystore credential nor a mock actuator: this level is unusable and will be reported unreachable.',
        ports: 'Host-facing switch ports',
        portsHint: "Comma-separated — ALL fire paths, sparing the node's BMC port (dedicated port, no shared-LOM)",
        outlets: 'PSU → outlet map',
        outletsHint: 'Comma-separated psu=outlet pairs — with dual PSUs BOTH outlets are switched off',
        roleCapable: 'Role-degradable account',
        roleCapableHint:
          "The device supports the write→read-only degrade of the peer's account (disarm-then-kill); without it the level never fires automatically (escalation-only, safe default)",
        escalationOnly: 'Escalation only',
        escalationOnlyHint: 'This level never fires in the automatic cascade — explicit operator arming only',
        reachable: 'Reachable',
        unreachable: 'Unreachable',
        moveUp: 'Move up',
        moveDown: 'Move down',
        remove: 'Remove level',
        add: 'Add device',
        save: 'Save cascade',
        saveTitle: 'Save the fencing cascade of {name}',
        saveWarning:
          'Fencing powers machines off: a wrong endpoint or map shoots the WRONG machine. Double-check every level before confirming.',
        unsaved: 'Unsaved changes — the cascade above is not what the store holds yet'
      }
    },

    // M2e (dettatura Luca 2026-08-03, 6°/7° giro): the cluster witness
    // configuration — the 2-node third vote, singleton PATCH (sudo,
    // cluster.update).
    witness: {
      title: 'Witness',
      hint: "The 2-node third vote: witnessd grants a takeover only when the peer's heartbeat is stale by TTL + margin; the grant is durable and epoch-fenced, and ONE grant covers both the md authority and the store promotion. Deployment requirement: reachable (also) on the DATA/client network — whoever has the clients wins.",
      none: 'No witness configured — automatic failover relies on the fencing cascade alone.',
      endpoints: 'Endpoints',
      endpointHint:
        'host:port — several entries are the SAME witness on several networks (multi-homed), not multiple witnesses',
      addEndpoint: 'Add endpoint',
      removeEndpoint: 'Remove endpoint',
      order: 'Proof order',
      orders: {
        witnessFirst: 'Witness first',
        fencingFirst: 'Fencing first'
      },
      orderHint: 'Which proof automatic failover tries first — the other stays the fallback',
      leaseTtlMs: 'Lease TTL (ms)',
      leaseTtlHint:
        'A holder silent this long becomes a takeover candidate — detection only: what happened to it is proven by fencing, not by elapsed time',
      renewMs: 'Renewal cadence (ms)',
      renewHint: 'The authority worker renews every held lease on this beat — must be below the TTL',
      marginMs: 'Grant margin (ms)',
      marginHint: "The takeover is granted only when the peer's heartbeat is stale by TTL + margin",
      reachable: 'Reachable',
      unreachable: 'Unreachable',
      notObserved: 'Not yet probed',
      configure: 'Configure',
      configureTitle: 'Configure the witness',
      enabled: 'Witness configured',
      enabledHint: 'Unchecked removes the witness — automatic failover falls back to the fencing cascade alone',
      saveWarning: 'The witness config decides who may take an authority in a partition — check the endpoints twice.'
    },

    // M5b-1 (ratifica 2026-08-04, #43): the cluster-wide monitoring settings —
    // the retention knob the backend expiry sweep reads on every pass.
    monitoring: {
      title: 'Monitoring and audit retention',
      hint: 'Cluster-wide retention, replicated with the cluster settings: the monitoring knob governs how long metric samples and resolved alerts are kept; the audit knob governs the API action registry (#73). They differ by default on purpose — an audit registry answers «who did this last quarter».',
      retentionDays: 'Monitoring retention (days)',
      retentionHint:
        '7 to 3650 days, default 90 — the expiry sweep reaps beyond it on every pass; lowering it removes the excess history on the next pass',
      auditRetentionDays: 'Audit retention (days)',
      auditRetentionHint:
        '90 to 3650 days, default 365, forward only (ENEA-A-R11: the window is a promise, a shorter value is refused) — the audit registry deliberately outlives the monitoring default; the expiry sweep is its only deleter',
      auditUseDefault: 'Use the platform default for audit (365 days)',
      configure: 'Configure',
      configureTitle: 'Configure retention',

      // ENEA-A-R11 (2026-10-23, N4): the sensitive-reads opt-in and the
      // forward-only audit window.
      sensitiveReads: 'Record sensitive reads',
      sensitiveReadsHint:
        'Opt-in: the GET of a credential, the catalog export and credentials of a share, the content and archive of share files leave a row of kind sensitiveRead — for the scopes listed. Off by default: the row volume is your choice.',
      sensitiveScopes: 'Scopes recorded (one selector per line)',
      sensitiveScopesHint: '/a exact, /a/* recursive, /* everything — the ?scope= grammar',
      useDefault: 'Use the platform default for monitoring (90 days)',
      saveWarning:
        'Lowering a retention deletes the history beyond it on the next sweep pass — this is not recoverable.',

      // ENEA-A-R12 (2026-10-23, §4): the baseline of the `anomaly` rules.
      anomalyTitle: 'Anomaly baseline',
      anomalyHint:
        'The baseline the «deviation from the baseline» rules compare against: a robust EWMA over the last hours of samples, a verdict only after the minimum number of samples, the EWMA weight auto (2/(N+1)) or fixed. No machine learning — thresholds are measured on iron (R18).',
      anomalyWindowHours: 'Baseline window (hours)',
      anomalyMinSamples: 'Minimum samples before a verdict',
      anomalyAlpha: 'EWMA weight α (empty = auto)',
      anomalyBounds: 'Window 1–168 h, samples 6–2016, α in (0, 1]',
      anomalySummary: '{hours} h · {samples} samples · α {alpha}',
      anomalyAlphaAuto: 'auto'
    },

    // ENEA-A-R1 N6 (2026-10-23): the transport-security profile card.
    // ENEA-A-R2 (2026-10-23, contract cluster.yaml `nfsEngine`): the NFS engine
    // of the Ceph gateways — Ganesha FSAL_CEPH (default) or the kernel NFS server.
    nfsEngine: {
      title: 'NFS engine (Ceph gateways)',
      hint: "Cluster-wide, replicated with the cluster settings: every Ceph gateway serves NFS with the same engine. ganesha is NFS-Ganesha FSAL_CEPH (the default); knfsd is the kernel NFS server re-exporting the gateway's kernel CephFS mount (NFSv4 root fsid=0, one bind per share, mounts with wsync). Switching is a planned act: each gateway stops one engine and starts the other, NFS sessions drop and reconnect. MultiSCSI shares keep Ganesha FSAL_VFS regardless.",
      engine: 'Engine',
      ganesha: 'Ganesha (FSAL_CEPH)',
      knfsd: 'kernel NFS (knfsd)',
      configure: 'Configure',
      configureTitle: 'Configure the NFS engine',
      useDefault: 'Use the platform default (Ganesha)',
      rdma: 'NFS over RDMA (knfsd)',
      rdmaPort: 'RDMA port',
      rdmaOn: 'RDMA wanted, port',
      rdmaOff: 'TCP only',
      rdmaHint:
        "Cluster-wide want; per node the reality wins: a gateway listens on RDMA only when it has an RDMA port in state ACTIVE (rdma link show) and the svcrdma module, otherwise it serves TCP and says so. The truth is the RDMA chip on the share's nfs daemon row (read from /proc/fs/nfsd/portlist). Clients mount with -o proto=rdma,port=<port>. Nothing happens under Ganesha.",
      saveWarning:
        'Changing the engine restarts NFS on every Ceph gateway: hard mounts pause and resume, soft mounts and open locks fail over the switch. Kerberos (krb5*) exports are served by Ganesha only in this round — under knfsd they are not exported and the gateway says so.'
    },

    // ENEA-A-R4 (2026-10-23, contract cluster.yaml `gatewayFailover`): the return
    // home of the Ceph gateways' floating addresses.
    // ENEA-A-R16 «drain» (2026-10-24): the orchestrated rolling update — ONE
    // parent task advanced by the backend sweep, persisted and resumed after
    // a backend restart; it stops at the first red gate.
    rollingUpdate: {
      title: 'Rolling update',
      hint: 'Node by node: gates (health, PG clean, replication links, addresses home, store) → drain → wait for the node to come back (restarted, and on the expected version when given) → undrain → gates → next node. It stops at the first red gate and never proceeds by itself. The update step itself belongs to the operator or the ISO: the product does not update itself.',
      operatorStep:
        'The update step is the operator’s or the ISO’s: reboot or reinstall the drained node, the orchestration waits for it to come back.',
      none: 'No rolling update in progress.',
      start: 'Start rolling update',
      startTitle: 'Start a rolling update of {name}?',
      startScale:
        'Every selected node will be drained in turn (non-destructive: roles and addresses only), then the orchestration waits for it to come back before undraining it and moving on. It stops at the first red gate and never proceeds by itself.',
      nodes: 'Nodes (in order)',
      nodesHint: 'Member nodes in name order — untick the ones to leave out',
      expectedVersion: 'Expected version (optional)',
      expectedVersionHint:
        'When given, a node counts as returned only on this version; otherwise a worker restart after the drain suffices',
      awaitMinutes: 'Wait for the return (minutes)',
      drainMinutes: 'Drain timeout (minutes)',
      minutesHint: '1-1440',
      confirmLabel: 'Type the cluster name to confirm',
      confirmHint: 'Exactly {name}',
      startConfirm: 'Start',
      started: 'Rolling update started — task {task}.',
      kind: 'State',
      kinds: {
        none: 'None',
        running: 'Running',
        paused: 'Paused',
        stopped: 'Stopped',
        finished: 'Finished'
      },
      progress: '{done} / {total} nodes done',
      current: 'Current node',
      plan: 'Plan',
      node: 'Node',
      state: 'Step',
      since: 'Since',
      detail: 'Detail',
      states: {
        pending: 'Pending',
        gating: 'Gates',
        draining: 'Draining',
        awaitingReturn: 'Awaiting return',
        undraining: 'Undraining',
        done: 'Done',
        failed: 'Failed',
        skipped: 'Skipped'
      },
      stoppedAt: 'Stopped at',
      awaitingHint:
        'Waiting for the node to come back: reboot or update it now — the orchestration resumes by itself when the worker restarts (and reports the expected version, when given).',
      expectedVersionNone: 'any (a restart suffices)',
      task: 'Task',
      taskLink: 'Open the parent task',
      pause: 'Pause',
      resume: 'Resume',
      abort: 'Abort',
      pauseTitle: 'Pause the rolling update?',
      pauseHint:
        'The step in flight finishes (a drain is never interrupted halfway); no new step opens until you resume.',
      resumeTitle: 'Resume the rolling update?',
      resumeHint: 'Back to the gates at the current node — also after a red gate, once you have fixed it.',
      abortTitle: 'Abort the rolling update?',
      abortScale:
        'The parent task is cancelled. The current node keeps the state it is in: if it is drained, bring it back with Undrain by hand.',
      abortConfirm: 'Abort',
      refusals: {
        nodes: 'Select at least one node',
        awaitMinutes: 'Whole minutes, 1-1440',
        drainMinutes: 'Whole minutes, 1-1440',
        confirm: 'Type the cluster name exactly'
      }
    },

    gatewayFailover: {
      title: 'Gateway address failover',
      hint: "How the floating addresses of the Ceph file gateways (CephCluster → gateway addresses) move back after a failover. The takeover of a dead gateway's address is never optional; the return home is a planned, brief interruption: automatic returns the address once the home node has been healthy for the given time (never during an NFS grace or while another lease is stale), manual leaves it where the takeover put it until the operator re-declares the cluster addresses.",
      rebalance: 'Return home',
      automatic: 'automatic',
      manual: 'manual',
      rebalanceAfterSec: 'After the home node is healthy for (s)',
      configure: 'Configure',
      configureTitle: 'Configure gateway address failover',
      useDefault: 'Use the platform default (automatic, 60 s)',
      saveWarning:
        'A return home moves an address between live gateways: hard NFS mounts pause and resume, open NFSv4 state is re-established by the client, SMB sessions on that address drop.'
    },
    transportSecurity: {
      title: 'Transport security',
      hint: "Cluster-wide profile, replicated with the cluster settings: with plainHttp = refused no WebDAV or FTP share can be declared in the clear (tls: none answers 422) and an object store without a certificate fails to converge instead of serving plain HTTP. allowed is today's behaviour (dev rigs).",
      plainHttp: 'Plain HTTP / FTP',
      allowed: 'allowed',
      refused: 'refused',
      configure: 'Configure',
      configureTitle: 'Configure transport security',
      useDefault: 'Use the platform default (allowed)',
      saveWarning:
        'Switching to refused does not tear down shares already declared in the clear; it gates new declarations and PATCHes, and object stores without a certificate stop converging until one is given.'
    },

    // #73 AUDIT-1 (#181 ondata 4): the API action registry — «la sezione
    // Audit sotto Cluster». HTTP method and status render VERBATIM (wire
    // literals, not vocabulary).
    audit: {
      title: 'Audit',
      hint: 'Every API mutation this cluster answered — who made it and what it answered, failed attempts included: a 401 login and a 403 refusal are outcomes, not omissions. Reads are never recorded, request bodies never stored. The registry is read-only by construction and lives on the cluster store: with the store down this list answers a speaking 503 on purpose — an empty page from a replica would look like a clean registry. Filters run server-side.',
      truncated: 'More history exists past these rows (newest first) — load older pages, or narrow with the filters.',
      loadMore: 'Load older events',
      occurredAt: 'When',
      who: 'Who',
      action: 'Action',
      route: 'Route',
      noRoute: 'No route matched (a path that does not exist)',
      status: 'Status',
      node: 'Answered by node',
      window: 'Time window',
      windows: {
        all: 'All history',
        '1h': 'Last hour',
        '24h': 'Last 24 hours',
        '7d': 'Last 7 days'
      },
      method: 'Method',
      methodAll: 'All methods',
      outcome: 'Outcome',
      outcomes: {
        all: 'All outcomes',
        ok: 'Succeeded (2xx/3xx)',
        denied: 'Refused (4xx)',
        failed: 'Server errors (5xx)'
      },
      username: 'Username',
      path: 'Path contains',
      applyHint: 'Enter applies',
      eventId: 'Event id',
      realm: 'Realm',
      sessionId: 'Session',
      userId: 'User id',
      remoteAddr: 'Caller address',
      unauthenticated: 'Unauthenticated attempt',

      // ENEA-A-R11 (2026-10-23, N4): the row kinds, the identity of a login
      // attempt, the user agent, the detail, and the hash chain's verify.
      kind: 'Kind',
      kindAll: 'All kinds',
      kinds: {
        mutation: 'Mutation',
        login: 'Login',
        sensitiveRead: 'Sensitive read',
        prune: 'Retention prune'
      },
      loginAttempt: 'login attempt',
      loginOutcome: 'outcome',
      userAgent: 'User agent',
      detail: 'Detail',
      hash: 'Chain hash',
      verify: {
        action: 'Verify chain',
        hint: 'Re-walks the per-node hash chain on the store: every row’s hash is recomputed from its fields and the previous row’s hash. Tamper EVIDENCE, not a signature — a party with write access to the store and the code could rewrite both.',
        rows: 'rows',
        chained: 'chained',
        unchained: 'unchained (older than the chain)',
        ok: 'chain intact',
        broken: 'CHAIN BROKEN — first break at row',
        startsAfterPrune: 'chain starts after the retention prune of {date} ({rows} rows removed)',
        failed: 'The verification could not run'
      }
    },

    // ENEA-A-R11 (2026-10-23, §1.2 / N5): log rotation and journald limits,
    // and the OpenMetrics scrape token.
    logging: {
      title: 'Logs and metrics',
      hint: 'Rotation of the product’s own log files (rendered as /etc/logrotate.d/trantor-tvstor on every node — the system logrotate timer applies it) and the journald limits (drop-in; takes effect at the next journald restart, which the worker does not perform). The read-only scrape token opens GET /metrics (OpenMetrics) to Prometheus; a session with monitoring.read scrapes too.',
      rotation: 'Log files',
      journal: 'Journal (journald)',
      maxSizeMiB: 'Rotate at (MiB)',
      maxAgeDays: 'Keep at most (days)',
      keep: 'Rotated copies',
      compress: 'Compress rotated files',
      journalMaxUseMiB: 'Journal size cap (MiB)',
      journalMaxRetentionDays: 'Journal retention (days)',

      // ENEA-A-R12 (2026-10-23, note 1 of the checkpoint): the client audit
      // file has its OWN cap, separate from the product logs — it must never
      // fill the system disk.
      auditFiles: 'Client audit file',
      auditMaxSizeMiB: 'Audit: rotate at (MiB)',
      auditKeep: 'Audit: rotated copies',
      auditHint:
        'The client audit events (share and store Audit tabs) have their own cap: 256 MiB × 8 copies by default, never the system disk. With the audit on over busy shares the syslog collector is where the stream lives; the local file is a queue.',
      configure: 'Configure',
      configureTitle: 'Configure log rotation',
      saveNote:
        'Applied by every node’s worker on its next pass. journald limits take effect at the next journald restart.',
      boundsHint:
        'Size 1–10240 MiB, age 1–3650 days, copies 1–365; journal 16–1048576 MiB, retention 1–3650 days; audit file 16–4096 MiB, copies 1–64',
      metricsTitle: 'OpenMetrics scrape token',
      tokenConfigured: 'A scrape token is configured',
      tokenMissing: 'No scrape token — GET /metrics answers only to a session with monitoring.read',
      issuedAt: 'issued {time}',
      issue: 'Issue token',
      rotate: 'Rotate token',
      revoke: 'Revoke',
      issueTitle: 'Issue the scrape token',
      issueWarning:
        'The token is shown ONCE, here. Issuing a new one invalidates the previous token immediately — every Prometheus using it stops scraping until updated.',
      tokenOnce: 'Copy it now — the console never shows it again (only its sha256 is stored).',
      snippet: 'Prometheus scrape_config',
      snippetHint:
        'The target is this console’s origin (it proxies /metrics to the API); the API origin itself answers the same scrape. The token travels as a Bearer — HTTPS only.',
      revokeTitle: 'Revoke the scrape token?',
      revokeWarning: 'Bearer scrapes answer 401 at once; a monitoring.read session still scrapes.',
      revoked: 'Scrape token revoked'
    },

    // WEBEXP-EDIT #63 (2026-08-05, ratifica Luca — Collabora/CODE via WOPI):
    // the external document-editor server, a settings leaf WITHOUT default —
    // null = office editing off, the explorer's mint answers a speaking 409.
    documentEditor: {
      title: 'Document editor',
      hint: "The external Collabora Online (CODE) server the file explorer's office editing rides via WOPI. Not configured means office editing is off — the explorer says so instead of showing a broken editor. The Collabora server must trust the cluster's TLS material (mount the cluster CA into its trust store); the platform-managed container is a declared follow-up.",
      serverUrl: 'Collabora server URL',
      serverUrlHint:
        'Base http(s) URL of the Collabora Online server (e.g. https://code.example.com) — the server MUST run with net.service_root=/api/v1/collabora (see the operator manual); the backend reads its discovery under that prefix to compose editor links',
      notConfigured: 'Not configured — office editing in the file explorer is off',
      configure: 'Configure',
      configureTitle: 'Configure the document editor',
      enabled: 'Document editor configured',
      enabledHint: 'Unchecked removes the configuration — office editing in the file explorer turns off',
      saveWarning:
        'The server named here receives short-lived editor tokens and the bytes of every file opened for editing — point it only at a Collabora server you trust.',

      // #181 T7 (#89 TLS-PIN): the editor's TLS anchor — without it a
      // non-loopback https editor cannot mint or proxy.
      certFingerprint: 'Server certificate fingerprint (TLS anchor)',
      certFingerprintHint:
        'sha-256 of the certificate the Collabora server serves, 64 hex characters (colons tolerated). Required for an https server outside loopback — a loopback server is anchored by its address, a plain-http one has no TLS to anchor.',
      pinRequired:
        'An https Collabora server outside loopback is never dialed without its certificate fingerprint — the editor mint and proxy would refuse naming this field.',

      // #181 ondata 3 (contract cluster.yaml wopiBaseUrl, round #68): the
      // call-back half — the base URL COLLABORA dials the cluster's WOPI
      // surface on. Its default is the co-located loopback, NEVER the
      // browser's Host echo (hairpin verified KO, 2026-08-06).
      wopiBaseUrl: 'WOPI callback base URL',
      wopiBaseUrlHint:
        "The base URL the COLLABORA SERVER uses to dial this cluster back ({base}/api/v1/wopi/files/…). Empty = https://127.0.0.1:<port>, the co-located server of the rig and the appliance — never the browser's own Host, which the editor container typically cannot reach. Set it when the Collabora server runs outside this node.",
      wopiBaseUrlDefault:
        'Default — https://127.0.0.1:<listen port> (co-located server); set a URL when Collabora runs outside this node'
    },

    // WIT-2 (2026-08-05, dettatura 2026-08-04 — 10° giro + addenda): the
    // cluster-level failover MODE at 3+ nodes — quorum vs fencing.
    failover: {
      title: 'Failover mode',
      hint: 'At 3+ nodes automatic failover becomes a cluster-level choice. Quorum (the default at 3+, mandatory with ceph): the cluster store’s majority arbitrates every authority — active fencing is disarmed, its credentials stay dormant, and below the majority the cluster stops. Fencing (opt-in, clusters without ceph only): the 2-node fence-and-exclude doctrine generalized — survives all-but-one node losses, at the price of verified fencing infrastructure on every node. At 2 nodes the effective mode is always fencing: the 2-node model is untouched.',
      mode: 'Declared mode',
      effectiveMode: 'Effective mode',
      modes: {
        auto: 'Automatic (by node count)',
        quorum: 'Quorum',
        fencing: 'Fencing'
      },
      modeHints: {
        auto: 'Resolves by node count alone: fencing at 2 members, quorum at 3+. The 3rd node’s join disarms fencing; shrinking back to 2 re-arms it.',
        quorum:
          'Store-majority arbitration at 3+ nodes. On a 2-node cluster it stays dormant — there is no majority to arbitrate with.',
        fencing:
          'Chained fence-and-exclude down to the last node standing. Refused while a ceph datapool exists, and only with every member’s fencing credentials verified.'
      },
      witness: 'Witness role',

      // #181 ondata 5: the role is DECLARED by the operator (ratifica
      // 2026-08-09), never platform-assigned — and «none» means nobody
      // declared it. The declaration lives in the configure dialog now: the
      // old CLI pointer fell with it.
      witnessNone:
        'None declared — the store majority arbitrates without a witness vote. The holder is an operator declaration (Configure), never platform-assigned.',
      degradedFallback:
        'The failover configuration names something that is no longer there — the declared witness holder is not a member any more; move the role by hand.',
      witnessRole: 'witness',

      // #181 ondata 5: «rendered rather than documented for one reason — the
      // level depends on THIS cluster's configuration» (contract).
      survivalLevel: 'Survival promise',
      survivalLevels: {
        'n-1': 'N-1 — survives one member loss (the honest promise without verified fencing credentials)',
        'n-2': 'N-2 and beyond — chained fence-and-exclude, fencing credentials verified on every member'
      },
      witnessSelect: 'Witness holder',
      witnessNoneOption: 'Nobody — no witness vote',
      witnessSelectHint:
        'An operator DECLARATION on a member, by name — never platform-assigned. Naming a non-member is refused with the members listed; if the declared holder later leaves, the cluster renders degraded with the repair named.',
      fencingState: 'Fencing state per node',
      armed: 'fencing armed',
      dormant: 'fencing dormant',
      notMember: 'not a member yet',
      configure: 'Configure',
      configureTitle: 'Configure failover',
      saveWarning:
        'The failover mode decides HOW an authority may ever be taken when a node dies. Opting into fencing is refused while a ceph datapool exists and demands verified fencing credentials on every node; quorum and automatic are always safe to set.'
    },

    // ENC-1 (2026-08-05, ratifica 2026-08-04 §1): the platform-managed
    // WireGuard tunnel on the replica network — the settings card.
    replicaEncryption: {
      title: 'Replica-network encryption',
      hint: 'A platform-managed WireGuard tunnel between the nodes on the replica network. Enabling mints one keypair per node (keystore, private half write-only) and migrates the internal NVMe/TCP transport onto the 169.254.77.0/24 overlay, one mirror leg at a time and only under fresh handshakes. The WireGuard UDP port must be open between the nodes. Store replication and DR shipping stay on the bare wire in this round (they migrate with the network-model round).',
      enabled: 'Enabled',
      disabled: 'Disabled',
      enabledLabel: 'Encrypt the replica network (WireGuard tunnel)',
      listenPort: 'UDP listen port',
      listenPortHint: '1-65535, default 51820 — the same port on every node; open it between the nodes',
      mtu: 'Tunnel MTU',
      mtuHint: '1280-9216; empty = kernel default (1420). Set with jumbo-frame replica networks',
      mtuDefault: 'kernel default',
      keepalive: 'Keepalive (s)',
      keepaliveHint: '0-3600, default 25; 0 disables — the handshake age is the tunnel health signal',
      nodeColumn: 'Node',
      overlayAddress: 'Overlay address',
      publicKey: 'Public key',
      configure: 'Configure',
      configureTitle: 'Configure replica-network encryption',
      saveWarning:
        'This changes the DATA PLANE: enabling migrates the mirror-leg transport onto the tunnel (one leg at a time, handshake-gated); the listen re-bind drops inbound legs once per node — prefer a quiet window. Disabling returns to the unencrypted wire.'
    },

    // M2f (2026-08-03): the Cluster page becomes tabbed — the store surface is
    // a topology of its own, not another card at the bottom of the registry.
    // M5b-1 (2026-08-04): the Settings tab joins — cluster-wide settings
    // (monitoring retention today; the security policy joins with its round).
    tabs: {
      nodes: 'Nodes',
      witness: 'Witness',
      store: 'Store',
      networks: 'Networks',
      audit: 'Audit',
      settings: 'Settings'
    },

    // NET-1 (2026-08-05, networks.yaml — charter ratified 2026-08-04; contract
    // shapes PROPOSAL where marked): the declared-network model — four
    // categories per node, per-node interface/bond bindings, observed link
    // state. Declaring a network migrates a surface off its dated address
    // seam; with none declared everything keeps riding the seams.
    networks: {
      title: 'Networks',
      hint: 'Declared networks in five categories: management (GUI/API), replicaInternal (mirror legs and the WireGuard tunnel endpoints), client (iSCSI portals, S3 endpoints, and the validation home of NAS service addresses and DHCP subnets), replicaExternal (cross-cluster DR), san (source address of the external-vdisk iSCSI/NVMe-oF logins toward third-party SANs). Declaring a network is what migrates a surface off the single-address seams; with none declared everything keeps working exactly as before. Bind nodes one by one — every mutation requires sudo, a wrong binding cuts the data plane.',
      name: 'Name',
      nameHint: 'Lowercase letters, digits and dashes',
      category: 'Category',
      categoryHint:
        'Immutable after creation — at most one replicaInternal and one replicaExternal network per cluster',
      categories: {
        management: 'Management',
        replicaInternal: 'Replica (internal)',
        client: 'Client',
        replicaExternal: 'Replica (external / DR)',
        san: 'SAN (external LUNs)'
      },
      vlanId: 'VLAN',
      vlanHint: '802.1Q tag 1-4094; empty = untagged',
      untagged: 'untagged',
      subnet: 'Subnet',
      subnetHint:
        'CIDR, IPv4 or IPv6 (e.g. 10.20.0.0/24, fd00:20::/64) — binding addresses must fall inside it; changeable only while unbound',
      mtu: 'MTU',
      mtuHint:
        '576-65520 (9216 on Ethernet carriers, 65520 on IPoIB connected mode — checked on the observed carrier at binding); empty = leave interface MTUs untouched',
      mtuUntouched: 'untouched',

      // #181 ondata 5: the real PATCH — name/vlan/mtu (reconcile on every
      // bound node), subnet only while unbound.
      editTitle: 'Edit network {name}',
      editBlipWarning:
        'A VLAN or MTU change converges via reconcile on EVERY bound node — a data-plane blip on that network. The category is immutable.',
      subnetBoundHint:
        'Changeable only while the network has no bindings — renumbering under live addresses is delete-and-redeclare',
      vlanEditHint: '1-4094; empty = back to untagged',
      mtuEditHint: '576-65520 (9216 on Ethernet, 65520 on IPoIB); empty = leave interface MTUs untouched',
      phase: 'Link health',
      phases: {
        ready: 'Ready',
        degraded: 'Degraded',
        unknown: 'Unknown'
      },
      bindings: 'Bindings',
      bindingsEmpty: 'No node bound yet — nothing rides this network until a binding lands',
      createTitle: 'Declare network',
      bindAction: 'Bind a node',
      bindTitle: 'Bind node to {name}',
      bindWarning:
        "This IS the data-plane switch: a replicaInternal binding re-homes the node's mirror legs (and WireGuard endpoint) onto the address, a client binding flips the node's iSCSI portal on the next reconcile. Replacing a binding is disruptive on replicaInternal.",
      node: 'Node',
      carrier: 'Carrier',
      carrierInterface: 'Plain interface',
      carrierBond: 'Platform-rendered bond',
      interface: 'Interface',
      interfaceHint:
        'Kernel netdev name (eth1, ens2f0) — not verified at admission: a missing device surfaces as a degraded binding',
      bondMembers: 'Bond members',
      bondMembersHint:
        'Comma-separated slave interfaces, at least two (eth1,eth2) — the bond device itself is minted by the platform',
      bondMode: 'Bond mode',
      bondModes: {
        activeBackup: 'active-backup (any switch)',
        lacp: '802.3ad LACP (switch-side LAG required)'
      },
      lacpRate: 'LACP rate',
      lacpRates: {
        default: 'kernel default (slow)',
        slow: 'slow (30 s)',
        fast: 'fast (1 s)'
      },
      address: 'Address',
      addressHint:
        'Bare IP inside the subnet, no prefix — unique fleet-wide (other bindings and share service addresses refuse it)',
      observedLink: 'Observed link',
      notObserved: 'Not yet observed',
      carrierUp: 'carrier up',
      carrierDown: 'carrier down',
      member: 'member',
      lacpPartner: 'LACP partner',

      // ENEA-A-R16 «InfiniBand» (2026-10-24): the IPoIB census chips and the
      // binding rules on an IB carrier (the server is the referee).
      ib: {
        transport: 'Transport',
        transports: {
          ethernet: 'Ethernet',
          infiniband: 'InfiniBand'
        },
        guid: 'GUID',
        pkey: 'P_Key',
        rdma: 'RDMA port',
        rate: 'Rate',
        lid: 'LID',
        rulesTitle: 'On an InfiniBand (IPoIB) carrier',
        rules: {
          noVlan:
            'No VLAN: 802.1Q does not exist on IPoIB — the partition is the P_Key (declared, not managed in v1): declare the P_Key interface (ib0.8001) as the carrier.',
          bondActiveBackup:
            'Bond in active-backup only: the kernel does no LACP on InfiniBand; mixed IB/Ethernet bonds are refused.',
          mtuCeilings:
            'MTU up to 65520 (connected mode) on InfiniBand, 9216 on Ethernet — the rule applies on the observed carrier.'
        },
        declared: 'Proven on the test double in the real formats; the fabric measure is R18.'
      },
      unbindAction: 'Remove a binding',
      unbindTitle: 'Unbind node from {name}',
      unbindWarning:
        "The explicit per-node exit: the platform tears down what it rendered (address, VLAN device, minted bond — never a declared physical interface) and the node's traffic for this category falls back to the seams. Disruptive on replicaInternal.",
      noBoundNodes: 'No bound nodes',
      delete: {
        title: 'Delete network {name}',
        warning:
          'Only an unbound network deletes — every binding must be removed first (each removal is the explicit per-node fallback to the seams). A client network also refuses while a share service address or DHCP subnet lives inside it.'
      },
      sudoRequired: 'Every network mutation requires sudo — it changes (or prepares to change) the data plane'
    },

    // M2f (dettatura Luca 2026-08-03, 8° giro — YugabyteDB dropped; politica
    // di ack RATIFICATA al 9° giro, stesso giorno): the cluster store's HA
    // surface. PostgreSQL managed by us at every scale — one primary plus N
    // streaming secondaries, ack on the whole ACK SET (strict-all),
    // platform-arbitrated promotion (witness grant or verified fence, never
    // the DB's own quorum). The M2f syncPolicy editor keys are retired with
    // the ANY-k policy they configured.
    // #189 GUI-RESIDUI (2026-08-19): the cluster-plane fields GET /cluster
    // always carried and no card rendered — quorum, convergence anchors,
    // epoch, stale fallback.
    plane: {
      quorum: 'Store quorum',
      quorumStates: {
        single: 'Single member',
        replicated: 'Replicated'
      },
      quorumHints: {
        single: 'One store member — the cluster-of-one: no replication, no ack set.',
        replicated: 'One primary plus N streaming secondaries, ack on the whole ack set (strict-all).'
      },
      configGeneration: 'Config generation',
      configGenerationHint:
        "The configuration's CURRENT generation — the convergence display's anchor. From the store when reachable; on the stale fallback it is this node's own applied generation.",
      minReplicatedGeneration: 'Fleet floor',
      minReplicatedGenerationHint:
        "The minimum of the members' config-generation watermarks — how far the slowest node has converged. Em dash while the store is unreachable or a member has not reported.",
      epoch: 'Epoch',
      epochHint:
        "The store's incarnation uuid — minted at formation, RE-MINTED by every promotion and emergency restore (the rejoin barrier's fencing signal). Nodes pin the epoch they belong to.",
      staleBanner:
        "STALE RENDER: the cluster store did not answer, so these fields were served from this node's LOCAL replica — the freshest truth it holds, not necessarily the cluster's."
    },

    // #189 GUI-RESIDUI: the security block — READ-ONLY BY CONTRACT. The
    // Settings round came (#196 SETTINGS-1, 2026-08-21) and could NOT open
    // it: PATCH /cluster still answers 422 immutable_field on `security`
    // (backend cluster/routes.ts «the security-policy write joins with its
    // own milestone») — declared, question carried in
    // docs/settings1-2026-08-21.md.
    security: {
      title: 'Security policy',
      hint: 'The cluster-wide security policy: stored in the replicated settings and EVALUATED by every node from its local replica — an outage never suspends enforcement.',
      passwordMinLength: 'Password minimum length',
      sessionMaxIdle: 'Session idle limit',
      lockoutThreshold: 'Lockout after',
      lockoutDuration: 'Lockout duration',
      characters: '{count} characters',
      minutes: '{count} minutes',
      attempts: '{count} failed attempts',
      readOnlyNote:
        'Read-only by contract: PATCH /cluster refuses the security block (422 immutable_field — «the security-policy write joins with its own milestone»). The Settings round (2026-08-21) opened the MFA knobs on the Users → MFA tab and left this block fenced: its write is a contract milestone of its own, declared — not a console gap.'
    },

    // #189 GUI-RESIDUI: notes on the SINGLETON — sudo, like every write of
    // this surface (one danger ladder for the singleton, 2026-08-04).
    notesCard: {
      title: 'Cluster notes',
      editTitle: 'Edit the cluster notes',
      editHint: 'Free-form operator text on the cluster itself — rendered as Markdown',
      sudoHint: 'The singleton PATCH is sudo-protected as a whole — one danger ladder, even for notes.'
    },

    store: {
      title: 'Cluster store',
      hint: 'The cluster store is PostgreSQL managed by TVStor: one primary plus N streaming-replication secondaries. A config write is confirmed only once EVERY member of the ack set has acked it (strict-all); a fallen secondary is fenced and excluded before writes resume, and re-enters the set only once caught up. A promotion is decided by the platform (witness grant or verified fencing), never by the database.',
      unavailable:
        'The store block is not available — either the store is unreachable, or you lack the cluster.store.read permission.',

      // The ack-set degradation vocabulary (ratifica 9° giro, 2026-08-03).
      degraded: 'Store degraded',
      degradedNoReason:
        "The store's durability is below its declared membership. See the ack set — fenced members are excluded, syncing ones not yet admitted.",
      degradedReasons: {
        'ack-blocked':
          'An ack-set member stopped acking — config commits are BLOCKED until its fence is verified and it is excluded (fence-before-degrade).',
        'fenced-excluded':
          'A secondary was fenced and excluded from the ack set. Writes continue — durability is reduced until it catches up and re-enters.',
        'no-secondary': 'No secondary is in the ack set — config writes are locally durable only.',
        'no-primary': 'No store member declares the primary role — the store is headless.',
        'promotion-in-flight': 'A store promotion is running — the ack set is being rebuilt around the new primary.'
      },

      primary: 'Primary',
      primaryUnknown: 'Unknown — the store render is stale',
      lastPromotedAt: 'promoted {time}',

      // #123 STORE-PROMSTAMP: the null stamp with the Unknown flag true —
      // «we no longer know», never presented as «never promoted».
      lastPromotedAtUnknown:
        'promotion date no longer known (row predates the stamp — repairs itself at the next promotion)',
      ackSet: 'Ack set',
      ackSetHint:
        'Every member listed here must ack every config commit (strict-all, ratified 9° giro). A fallen member is fenced and excluded; a new or returning member joins only once caught up.',
      ackSetEmpty: 'empty — no synchronous durability',
      ack: 'Ack',
      ackStates: {
        in: 'In ack set',
        fenced: 'Fenced',
        syncing: 'Syncing'
      },
      ackStateHints: {
        in: 'In the durability set — every config commit waits for this member',
        fenced:
          'Verified-fenced and EXCLUDED from the ack set — a resync target, never auto-promoted (only the confirm-typed promotion may)',
        syncing: 'Out of the ack set until caught up — a fresh basebackup or a fenced returner mid-catch-up'
      },
      standbyNames: 'synchronous_standby_names',
      standbyNamesHint:
        'What the reconcile pass has APPLIED on the primary — expected from the current ack set: {expected}',
      standbyNone: 'empty — no synchronous ack required',

      node: 'Node',
      role: 'Role',
      roles: {
        primary: 'Primary',
        secondary: 'Secondary',
        none: 'Not a member'
      },
      roleHints: {
        primary: "The writable instance the whole cluster's store handle resolves to",
        secondary:
          "A streaming standby, in recovery — never served as the store: the handle's health probe rejects a candidate in recovery",
        none: 'Not a store member'
      },
      syncState: 'Sync state',
      syncStates: {
        sync: 'Sync',
        quorum: 'Quorum',
        potential: 'Potential',
        async: 'Async',
        disconnected: 'Disconnected',
        unknown: 'Unknown'
      },
      syncStateHints: {
        sync: 'Acking synchronously — config commits wait for this member (in the applied FIRST-n list)',
        quorum: 'Part of a quorum ack set — a foreign ANY-k configuration (TVStor renders FIRST-n)',
        potential: 'Configured but not currently promised — a foreign ANY-k configuration',
        async: 'Streaming outside the ack set — it never blocks a commit and can fall behind',
        disconnected: 'The slot exists and nobody is streaming — WAL is being pinned on the primary',
        unknown: 'Not observed yet'
      },
      lag: 'Lag (WAL / replay)',
      lagHints: {
        unknown: 'Not observed yet',
        ok: 'Aligned — a promotion here would lose nothing observable',
        warning: 'Falling behind — a promotion here may lose recent config writes',
        critical: 'Far behind — promoting this member would lose config writes; check the link and the instance first'
      },
      notObserved: 'Not observed',
      slot: 'Replication slot',
      endpoint: 'Endpoint',
      observedAt: 'Observed at',
      membersEmpty: 'No store member observed yet',

      addMember: 'Add member',
      addMemberTitle: 'Add a store member',
      addMemberHint:
        'The node gets a replication slot on the primary, a pg_basebackup and a worker-supervised PostgreSQL instance. It joins the ack set only once streaming and caught up — the initial copy never blocks config writes.',
      addMemberNodeHint: 'A registered node that is not a store member yet',
      addMemberSubmit: 'Add member',
      noCandidates: 'Every registered node is already a store member',
      noPrimaryBlock: 'The store has no reachable primary — a member cannot be born from nothing',

      promote: 'Promote',
      promoteTitle: 'Promote {name} to store primary',
      promoteWarning:
        'The promotion re-mints the cluster epoch: every other member converges down onto the new primary, and the old primary is refused primary-hood on return and re-attaches as a secondary. It is refused unless a witness grant or a verified fence still proves the current primary is stopped.',
      promoteConfirmLabel: 'Node name',
      promoteConfirmHint: 'Type {name} to confirm the promotion',
      promoteFencedWarning:
        'This member is FENCE-EXCLUDED from the ack set: it is not guaranteed current, and promoting it LOSES config writes that were already acked. This confirm-typed promotion is the only path that may do it — the automatic failover refuses it (twin of forcePromote).',
      promoteSubmit: 'Promote',
      promotePrimaryBlock: 'Already the primary',

      removeMember: 'Remove',
      removeMemberTitle: 'Remove {name} from the store?',
      removeMemberWarning:
        'The membership is cleared first (while the member still acks — strict-all), then the instance is stopped and its replication slot is DROPPED on the primary — a slot left behind pins WAL forever. Rejoining later means a fresh basebackup.',
      removeMemberSubmit: 'Remove member',
      removalProblems: {
        'is-primary': 'The primary is never removed — promote another member first, the store is never left headless'
      }
    }
  }
}

export const it: typeof en = {
  cluster: {
    nodes: {
      title: 'Nodi',
      name: 'Nome',

      // #204 CEPH-STRETCH Fase A (2026-08-24): l'etichetta di topologia multi-sito.
      site: 'Sito',
      phase: 'Fase',
      phases: {
        'pending-join': 'In attesa di join',
        joining: 'In ingresso',
        joinPending: 'In attesa di conferma',
        member: 'Membro'
      },
      storeMember: 'Store del cluster',
      replicatedGeneration: 'Generazione replicata',
      services: 'Servizi',

      // ENC-1 (2026-08-05): la colonna colpo-d'occhio del tunnel di replica.
      tunnel: 'Tunnel di replica',
      tunnelUp: 'Attivo',
      tunnelPeersDown: '{n} peer giù',
      lastSeen: 'Ultimo contatto',
      neverSeen: 'Mai visto',

      // ENEA-A-R16 «drain» (2026-10-24): la foglia di manutenzione — chip di
      // stato, i due verbi (conferma scritta, sudo), ceduto e mancante.
      maintenance: {
        column: 'Manutenzione',
        tabTitle: 'Manutenzione',
        states: {
          draining: 'In drain',
          maintenance: 'In manutenzione',
          undraining: 'In rientro',
          inService: 'In servizio'
        },
        state: 'Stato',
        since: 'Da',
        hours: '{h} h',
        requestedBy: 'Richiesto da',
        reason: 'Motivo',
        task: 'Task',
        taskLink: 'Apri il task',
        enteredAt: 'In manutenzione da',
        nonDestructive:
          'Il drain non è mai distruttivo: nessun OSD rimosso, nessun dato mosso — solo ruoli e indirizzi passano di mano (gli indirizzi flottanti dei gateway dopo una grazia RADOS, l’autorità dei volumi multiscsi al peer, i ruoli di controllo al membro successivo; `noout` per la durata).',
        drainAction: 'Drain (manutenzione)',
        undrainAction: 'Undrain (torna in servizio)',
        drainTitle: 'Drenare {name} per manutenzione?',
        undrainTitle: 'Riportare {name} in servizio?',
        drainScale:
          'Il nodo cede gli indirizzi flottanti dei gateway (prima una grazia RADOS), l’autorità dei volumi multiscsi (il peer subentra) e i ruoli di controllo, mette `noout` sui suoi cluster Ceph e ferma i gateway consolidati quando gli indirizzi sono passati. Niente viene distrutto. Cancelli di ammissione: quorum dei MON, un peer sano per ogni volume servito, il ruolo nello store.',
        undrainScale:
          '`noout` viene tolto; i battiti tornano a candidarsi — gli indirizzi tornano a casa secondo la politica di failover dei gateway (automatico dopo l’attesa, mai con manuale); i volumi multiscsi RESTANO sul peer (usa i verbi di failover per riportarli).',
        confirmLabel: 'Scrivi il nome del nodo per confermare',
        confirmHint: 'Esattamente {name}',
        reasonLabel: 'Motivo (facoltativo)',
        reasonHint: 'Resta sulla foglia di manutenzione — il riavvio, il firmware, il ticket',
        drainConfirm: 'Drain',
        undrainConfirm: 'Undrain',
        started: 'Task {task} avviato — il dettaglio del nodo mostra cosa è ceduto e cosa manca.',
        startedNoTask: 'Accettato — il dettaglio del nodo mostra cosa è ceduto e cosa manca.',
        ceded: {
          title: 'Ceduto',
          addresses: 'Indirizzi di gateway',
          volumes: 'Volumi (autorità multiscsi)',
          roles: 'Ruoli di controllo',
          noout: 'OSD noout',
          nooutOn: 'impostato',
          nooutOff: 'non impostato',
          none: 'Niente registrato ancora'
        },
        pending: {
          title: 'Ancora tenuto',
          addresses: 'Indirizzi',
          volumes: 'Volumi',
          roles: 'Ruoli',
          settled: 'Niente è più tenuto — il nodo ha ceduto tutto.',
          unknown: 'Non riportato da questo backend'
        },
        roles: {
          control: 'nodo di controllo',
          catalog: 'catalogo',
          mirror: 'cephfs-mirror',
          forwarder: 'forwarder'
        },
        software: {
          title: 'Software',
          version: 'Versione',
          startedAt: 'Worker avviato',
          unknown: 'Non ancora riportata (un worker precedente a questa foglia)',
          returned: 'Tornato dopo l’ingresso in manutenzione',
          notReturned: 'Non riavviato dall’ingresso in manutenzione'
        },
        inServiceHint: 'Il nodo è in servizio: nessuna foglia di manutenzione.'
      },
      tabOverview: 'Panoramica',
      tabDisks: 'Dischi (osservati)',
      certs: {
        revokeAction: 'Revoca i certificati di cluster del nodo',
        revokeTitle: 'Revocare i certificati di {name}?',
        revokeScale:
          "Ogni certificato foglia in essere emesso a questo nodo finisce sulla lista di revoca firmata dalla CA, e il nodo viene marcato così che non gli venga più emesso nulla. Contano entrambe le metà: la lista ritira ciò che era già emesso — ogni nodo la verifica OFFLINE durante l'handshake TLS — e la marca impedisce al worker del nodo espulso di ottenere un serial fresco entro il minuto.",
        revokeWindow:
          'Il costo dichiarato: ogni peer applica la lista CHE HA — la revoca raggiunge un peer entro un periodo di refresh della CRL (più il tempo in cui non raggiunge lo store). La metrica node.crlAgeSeconds mostra chi è ancora indietro. Idempotente: premere due volte è sicuro.',
        revokeConfirm: 'Revoca certificati',
        revokeDone:
          '{count} certificato/i revocato/i; la lista di revoca è ora alla generazione {crl}. Ogni nodo riporta questo numero quando la recepisce — guarda node.crlAgeSeconds per chi è ancora indietro.',
        revokeZeroHint:
          '0 significa che il nodo non aveva più nulla da revocare — un nodo già revocato, o uno che non ha mai ottenuto una foglia. La marca è impostata comunque.',
        reinstateAction: 'Consenti di nuovo i certificati al nodo',
        reinstateTitle: 'Riammettere i certificati di {name}?',
        reinstateScale:
          'Cancella la marca di revoca: il nodo ottiene un certificato FRESCO alla prossima passata PKI. Nulla esce dalla lista di revoca, di proposito — i serial revocati restano revocati fino alla scadenza naturale: riammettere il NODO non rende di nuovo affidabile un CERTIFICATO rubato.',
        reinstateConfirm: 'Riammetti',
        reinstateDone:
          'Marca cancellata — il nodo ottiene un certificato fresco alla prossima passata PKI (quelli revocati restano revocati).'
      },
      join: {
        title: 'Pre-registra un nodo',
        hint: "Conia il token di join monouso che il nuovo nodo presenta durante il suo handshake. Il nome DEVE essere uguale a quello che sceglierà l'installazione del nodo; il token vive 30 minuti, è conservato hashato lato server, ed è RESO UNA VOLTA SOLA in questo dialog — perderlo significa revocare e ri-registrare. La registrazione non scade col suo token: una riga col token scaduto resta visibile (registry onesto) finché non viene revocata o il nodo non entra.",
        name: 'Nome del nodo',
        nameHint: "Deve essere uguale al nome che sceglierà l'installazione del nuovo nodo",
        namePattern: 'Minuscole, cifre e trattini; lettera o cifra ai bordi (max 63)',
        address: 'Indirizzo di management',
        addressHint: "L'indirizzo che il cluster chiama per l'handshake di join",
        confirm: 'Conia il token di join',
        shownOnce:
          "Questo token è mostrato UNA VOLTA SOLA — esiste solo in questa risposta. Verifica il fingerprint della CA fuori banda sul nuovo nodo prima di fidarti dell'handshake. Se perdi il token, revoca la registrazione e ri-registra: i token non vengono mai ri-emessi per una registrazione viva.",
        token: 'Token di join',
        caFingerprint: 'Fingerprint della CA del cluster',
        caHint: "Incorporato anche nel token — reso a parte per la lettura dell'operatore e per gli script",
        expires: 'Scade {time} (conio + 30 minuti, monouso)',
        revokeAction: 'Revoca questa registrazione e il suo token',
        revokeTitle: 'Revocare la registrazione di {name}?',
        revokeScale:
          "La riga joining e il suo token di join muoiono nella stessa scrittura di config — un token revocato fallisce l'handshake anche dentro il suo TTL. NON è la rimozione di un nodo: solo le registrazioni joining sono cancellabili (drenare un membro vero è un round a parte con la sua catena di task).",
        revokeConfirm: 'Revoca registrazione'
      },
      disks: {
        device: 'Device',
        serial: 'Seriale',
        wwid: 'WWID',
        noWwid: 'non esposto',
        wear: 'Usura',
        wearHint:
          'Il contatore di usura del firmware del disco (percentuale consumata) letto da smartctl ogni 15 minuti — sopra 80 % warning, sopra 90 % critical: pianificare la sostituzione',
        wearUnread: 'Non ancora letto',
        wearUnavailable: 'n/d',
        wearNoCounter: 'nessun contatore',
        wearNoCounterHint:
          'SMART ha risposto ma questo disco non espone un contatore di usura (tipico dei dischi rotanti)',
        mediaErrors: 'errori media',
        paths: 'Cammini',
        pathActive: 'su',
        pathFailed: 'GIÙ',
        pathUnknown: '?',
        seenBy: 'Visto da',
        shared: 'CONDIVISO (non usabile su un nodo fino alla fase 2 di JBOD-DC)',
        size: 'Dimensione',
        model: 'Modello',
        state: 'Stato',
        observedAt: 'Osservato il',
        marked: 'Marcato usabile',
        inUse: 'In uso',
        free: 'Libero',
        noSerial: 'Nessun seriale esposto — non marcabile',
        empty: 'Ancora nessun device osservato',
        notObserved: "Marcato ma assente dall'inventario osservato",

        // Osservazione nel dettaglio nodo (dettatura Luca 2026-08-06): il tab
        // è una semplice lettura dei device trovati — la gestione vive nelle
        // pagine Storage dedicate, linkate dall'intestazione del tab.
        observedHint:
          'Osservazione in sola lettura: ciò che la scansione ha trovato su questo nodo — inclusi il flag marcato usabile e il censimento chunk. La gestione dei dischi (marcatura, chunk, raid) e dei vdisk vive nelle pagine Storage.',
        manage: 'Gestisci dischi →',
        manageVdisks: 'Gestisci vdisk →',
        hint: 'Marcare è configurazione — niente tocca il device finché Crea chunk non lo ritaglia. Un disco ritagliato diventa un PV nel suo volume group (un VG per disco: un disco muore da solo), diviso in chunk anonimi di una taglia fissa per-disco; raid e vdisk jbod li consumano per quantità.',
        chunks: 'Chunk',
        notChunked: 'Non a chunk',
        chunkRegime: '{total} × {size} GiB',
        chunkRegimeUnknown: '? × {size} GiB — totale non ancora confermato',
        chunkBar: '{allocated} allocati · {free} liberi',
        chunkPhases: {
          pending: 'In attesa',
          creating: 'In ritaglio',
          ready: 'Pronto',
          error: 'Errore',
          destroying: 'In smantellamento'
        },
        createChunks: 'Crea chunk',
        chunkSizeGib: 'Taglia dei chunk (GiB)',
        dechunk: 'Smantella i chunk',
        dechunkTitle: 'Smantellare il regime chunk di {serial}?',
        dechunkWarning:
          'Lo strato chunk SVUOTATO del disco viene rimosso (vgremove/pvremove) e il disco torna semplice disco marcato usabile — ritagliabile a una nuova taglia. Chunk allocati lo rifiutano: prima distruggi i raid e i vdisk che li consumano.',
        dechunkConfirmLabel: 'Seriale del disco',
        dechunkConfirmHint: 'Digita {serial} per confermare lo smantellamento',
        dechunkAllocatedBlock: 'Ci sono ancora chunk allocati — prima distruggi i raid/vdisk che li consumano'
      },
      raids: {
        hint: "Un membro raid è un AGGREGATO per-disco: K chunk liberi di un disco concatenati in un LV — md vede un device per disco. Onestà (la lezione SHR): array che condividono dischi via chunk si contendono gli IOPS durante un rebuild, e un raid0 perde l'intero device con UNO QUALSIASI dei suoi dischi membri.",
        create: 'Crea raid',
        makeTitle: 'Crea un raid su {name}',
        name: 'Nome',
        nameHint: 'Minuscole, cifre e trattini — unico per nodo',
        level: 'Livello',
        levelHint: '{level} richiede almeno {min} dischi membri',
        members: 'Aggregati membri',
        membersHint:
          'Indica per ogni disco membro quanti dei suoi chunk liberi aggregare — 0 lo esclude. I livelli ridondanti richiedono la STESSA taglia aggregata in byte su ogni disco (ogni disco conta la sua taglia di chunk); raid0 ammette aggregati diversi.',
        memberFree: '{free} liberi × chunk da {size} GiB',
        memberAggregate: 'aggregato {gib} GiB',
        noDisks: 'Meno di due dischi a chunk con chunk liberi — ritaglia i chunk nel tab Dischi dati prima.',
        problems: {
          'min-disks': '{level} richiede almeno {min} dischi membri — {got} selezionati',
          'over-free': 'Solo {free} chunk liberi su {disk}',
          'unequal-aggregates': 'I livelli ridondanti richiedono la stessa taglia aggregata su ogni disco: {detail}'
        },
        empty: 'Ancora nessun raid',
        phase: 'Fase',
        phases: {
          pending: 'In attesa',
          creating: 'In creazione',
          ready: 'Pronto',
          degraded: 'Degradato',
          error: 'Errore',
          destroying: 'In distruzione'
        },
        vdisk: 'Vdisk',
        claimed: 'Reclamato',
        unclaimed: 'Non reclamato',
        mdState: 'Stato array',
        size: 'Dimensione',
        observedAt: 'Osservato il',
        notObserved: 'Non ancora osservato',
        destroy: 'Distruggi',
        destroyTitle: 'Distruggere il raid {name}?',
        destroyWarning:
          "L'array md viene fermato, i suoi superblocchi cancellati e gli LV aggregati rimossi — i chunk tornano liberi sui loro dischi. L'operazione non è reversibile.",
        destroyConfirmLabel: 'Nome del raid',
        destroyConfirmHint: 'Digita {name} per confermare la distruzione',
        claimedBlock:
          'Reclamato da un vdisk — prima distruggi il vdisk (che rilascia soltanto il raid; è questa distruzione a smantellarlo)',
        destroyingBlock: 'Distruzione già in corso'
      },
      vdisks: {
        create: 'Crea vdisk',
        makeTitle: 'Crea un vdisk su {name}',
        name: 'Nome',
        nameHint: 'Minuscole, cifre e trattini — unico per nodo',
        source: 'Sorgente',
        sourceHint: 'Un vdisk avvolge un raid, oppure concatena chunk di UN disco (jbod) — scelta ora, immutabile',
        forms: {
          raid: 'RAID — avvolgi un raid',
          jbod: 'JBOD — chunk di un disco',
          external: 'EXTERNAL — una LUN di una SAN terza'
        },
        extTransport: 'Trasporto',
        extTransportHint: 'iSCSI (dm-multipath sui portali) o NVMe-oF (multipath nativo ANA)',
        extPortals: 'Portali',
        extPortalsHint:
          'host[:porta], separati da virgola — più portali = più path verso la STESSA LUN (porta default 3260 iSCSI / 4420 NVMe-oF)',
        extIqn: 'IQN del target',
        extIqnHint: 'Il target lato SAN che offre la LUN',
        extLun: 'LUN',
        extLunHint: 'Numero di LUN (vuoto = 0)',
        extNqn: 'NQN del subsystem',
        extNqnHint: 'Il subsystem NVMe lato SAN',
        extNsid: 'NSID',
        extNsidHint: 'Id del namespace (vuoto = il primo namespace)',
        extCredential: 'Credenziale CHAP',
        extCredentialHint: 'CHAP lato initiator — una credenziale iSCSI CHAP esistente del keystore',
        extCredentialNone:
          'Nessuna credenziale iSCSI CHAP nel keystore (creane una in Sistema → Credenziali, o accedi senza CHAP)',
        extNoCredential: 'Nessuna — senza CHAP',
        extWwid: 'WWID',
        extWwidHint:
          "L'identità della LUN quando la conosci (vuoto: osservata al primo login e riportata sulla foglia)",
        extWwidUnobserved: 'Non ancora osservato',
        extHonesty:
          'La tolleranza ai guasti-disco di un vdisk external è quella della SAN terza; TVStor aggiunge la ridondanza di nodo/sito coi modelli esistenti sopra (volumi mirror fra vdisk). LUKS2 a riposo non è disponibile su questa forma.',
        extPaths: 'Path',
        extPathsEmpty: 'Guardato — la SAN non offre alcun path (perdita dichiarata)',
        extPathsUnknown:
          'Censimento path non preso — non si è potuto guardare (multipathd/nvme-cli muto o login non stabilito)',
        raid: 'Raid',
        raidHint: 'Un raid pronto e non reclamato di questo nodo — il suo device md diventa il device del vdisk',
        noRaids: 'Nessun raid pronto libero su questo nodo',
        disk: 'Disco',
        diskHint: 'Un disco a chunk con chunk liberi — un vdisk jbod non attraversa mai i dischi',
        chunksCount: 'Chunk',
        chunksCountHint: '{free} liberi × chunk da {size} GiB su {disk}',
        noSources:
          'Niente su cui costruire — ritaglia i chunk nel tab Dischi dati (e opzionalmente crea un raid) prima.',
        claimed: 'Reclamato',
        empty: 'Ancora nessun vdisk',
        encryption: 'Cifratura a riposo',
        encryptedChip: 'Cifrato',
        lockedChip: 'BLOCCATO',
        lockedReason: 'La chiave non è risolvibile dal keystore — il vdisk non serve dati',
        openChip: 'Sbloccato',
        phase: 'Fase',
        phases: {
          pending: 'In attesa',
          creating: 'In creazione',
          ready: 'Pronto',
          degraded: 'Degradato',
          error: 'Errore',
          destroying: 'In distruzione'
        },
        datapool: 'Datapool',
        unclaimed: 'Non reclamato',
        deviceState: 'Stato device',
        size: 'Dimensione',
        observedAt: 'Osservato il',
        notObserved: 'Non ancora osservato',
        destroy: 'Distruggi',
        destroyTitle: 'Distruggere il vdisk {name}?',
        destroyWarning:
          "Il device del vdisk viene ritirato: una mappatura cifrata viene chiusa e la sua chiave riassorbita. Un vdisk su raid RILASCIA soltanto il suo raid (l'array sopravvive, ri-reclamabile — a smantellarlo è la distruzione del raid); di un vdisk jbod viene rimosso l'LV e i suoi chunk tornano liberi. L'operazione non è reversibile.",
        destroyConfirmLabel: 'Nome del vdisk',
        destroyConfirmHint: 'Digita {name} per confermare la distruzione',
        claimedBlock: 'Reclamato da un datapool — prima distruggi il datapool',
        destroyingBlock: 'Distruzione già in corso'
      },
      fencing: {
        tabTitle: 'Fencing',
        hint: 'La cascata ORDINATA che gli ALTRI nodi usano per fare fencing di QUESTO nodo: vince il primo livello con esito VERIFICATO; i livelli successivi scattano solo se il precedente è irraggiungibile o non verificabile. Un fencing dichiarato male spara alla macchina sbagliata — il salvataggio richiede sudo.',
        empty:
          'Nessun device di fencing dichiarato — prendere automaticamente i volumi di questo nodo dipende dal solo witness, o resta manuale.',
        kind: 'Tipo',
        kinds: {
          bmc: 'BMC',
          switch: 'Switch',
          pdu: 'PDU'
        },
        endpoint: 'Endpoint',
        endpointHint: 'Indirizzo di management — base URL Redfish per una BMC',
        credential: 'Account del device (keystore)',
        credentialNone: 'Nessuno — solo attuatore mock/rig',
        credentialHint:
          "Una credenziale bmcAccount del keystore, risolta al momento dell'uso — letture, kill E self-disarm: il suo username è l'account il cui ruolo viene degradato",
        credentialMissing:
          'Né credenziale del keystore né attuatore mock: questo livello è inutilizzabile e verrà riportato irraggiungibile.',
        ports: 'Porte switch lato host',
        portsHint:
          'Separate da virgola — TUTTI i percorsi di fuoco, risparmiando la porta BMC del nodo (porta dedicata, niente shared-LOM)',
        outlets: 'Mappa PSU → outlet',
        outletsHint: 'Coppie psu=outlet separate da virgola — con doppia PSU si spengono ENTRAMBI gli outlet',
        roleCapable: 'Account degradabile per ruolo',
        roleCapableHint:
          "Il device supporta il degrade write→read-only dell'account del peer (disarm-then-kill); senza, il livello non spara mai automaticamente (solo escalation, default sicuro)",
        escalationOnly: 'Solo escalation',
        escalationOnlyHint:
          "Questo livello non spara mai nella cascata automatica — solo arming esplicito dell'operatore",
        reachable: 'Raggiungibile',
        unreachable: 'Irraggiungibile',
        moveUp: 'Sposta su',
        moveDown: 'Sposta giù',
        remove: 'Rimuovi livello',
        add: 'Aggiungi device',
        save: 'Salva cascata',
        saveTitle: 'Salva la cascata di fencing di {name}',
        saveWarning:
          'Il fencing spegne macchine: un endpoint o una mappa sbagliati sparano alla macchina SBAGLIATA. Ricontrolla ogni livello prima di confermare.',
        unsaved: 'Modifiche non salvate — la cascata qui sopra non è ancora quella dello store'
      }
    },
    witness: {
      title: 'Witness',
      hint: "Il terzo voto del 2-nodi: witnessd concede il takeover solo quando l'heartbeat del peer è vecchio di TTL + margine; il grant è durevole ed epoch-fenced, e UN grant copre sia l'authority md sia la promozione dello store. Requisito di deployment: raggiungibile (anche) sulla rete DATI/client — vince chi ha i client.",
      none: 'Nessun witness configurato — il failover automatico dipende dalla sola cascata di fencing.',
      endpoints: 'Endpoint',
      endpointHint: 'host:porta — più voci sono lo STESSO witness su più reti (multi-homed), non più witness',
      addEndpoint: 'Aggiungi endpoint',
      removeEndpoint: 'Rimuovi endpoint',
      order: 'Ordine delle prove',
      orders: {
        witnessFirst: 'Prima il witness',
        fencingFirst: 'Prima il fencing'
      },
      orderHint: "Quale prova il failover automatico tenta per prima — l'altra resta il fallback",
      leaseTtlMs: 'TTL del lease (ms)',
      leaseTtlHint:
        'Un detentore silenzioso per questo tempo diventa candidato al takeover — è solo rilevazione: cosa gli sia successo lo prova il fencing, non il tempo trascorso',
      renewMs: 'Cadenza di rinnovo (ms)',
      renewHint: "Il worker dell'authority rinnova ogni lease detenuto a questo ritmo — deve stare sotto il TTL",
      marginMs: 'Margine di grant (ms)',
      marginHint: "Il takeover è concesso solo quando l'heartbeat del peer è vecchio di TTL + margine",
      reachable: 'Raggiungibile',
      unreachable: 'Irraggiungibile',
      notObserved: 'Non ancora sondato',
      configure: 'Configura',
      configureTitle: 'Configura il witness',
      enabled: 'Witness configurato',
      enabledHint: 'Deselezionato rimuove il witness — il failover automatico ripiega sulla sola cascata di fencing',
      saveWarning:
        "La config del witness decide chi può prendere un'authority in una partizione — controlla due volte gli endpoint."
    },
    monitoring: {
      title: 'Retention di monitoraggio e audit',
      hint: "Retention a livello di cluster, replicata con le impostazioni: la manopola di monitoraggio governa per quanto tempo campioni metrici e alert risolti vengono conservati; quella di audit governa il registro delle azioni API (#73). I default differiscono di proposito — un registro audit risponde a «chi l'ha fatto il trimestre scorso».",
      retentionDays: 'Retention di monitoraggio (giorni)',
      retentionHint:
        'Da 7 a 3650 giorni, default 90 — lo sweep di scadenza rimuove oltre questa soglia a ogni passata; abbassarla elimina lo storico in eccesso alla passata successiva',
      auditRetentionDays: 'Retention di audit (giorni)',
      auditRetentionHint:
        'Da 90 a 3650 giorni, default 365, solo in avanti (ENEA-A-R11: la finestra è una promessa, un valore più corto è rifiutato) — il registro audit sopravvive di proposito al default di monitoraggio; lo sweep di scadenza è il suo unico cancellatore',
      auditUseDefault: "Usa il default di piattaforma per l'audit (365 giorni)",
      configure: 'Configura',
      configureTitle: 'Configura la retention',
      sensitiveReads: 'Registra le letture sensibili',
      sensitiveReadsHint:
        "Opt-in: la GET di una credenziale, l'export e le credenziali del catalogo di una share, il contenuto e l'archivio dei file di una share lasciano una riga di tipo sensitiveRead — per gli scope elencati. Spento di default: il volume di righe lo scegli tu.",
      sensitiveScopes: 'Scope registrati (un selettore per riga)',
      sensitiveScopesHint: '/a esatto, /a/* ricorsivo, /* tutto — la grammatica di ?scope=',
      useDefault: 'Usa il default di piattaforma per il monitoraggio (90 giorni)',
      saveWarning:
        'Abbassare una retention elimina lo storico oltre soglia alla passata successiva dello sweep — non è recuperabile.',
      anomalyTitle: 'Baseline delle anomalie',
      anomalyHint:
        'La baseline con cui le regole «scostamento dalla baseline» confrontano: una EWMA robusta sulle ultime ore di campioni, un verdetto solo dopo il minimo di campioni, il peso EWMA automatico (2/(N+1)) o fisso. Niente machine learning — le soglie si misurano sul ferro (R18).',
      anomalyWindowHours: 'Finestra della baseline (ore)',
      anomalyMinSamples: 'Campioni minimi prima di un verdetto',
      anomalyAlpha: 'Peso EWMA α (vuoto = automatico)',
      anomalyBounds: 'Finestra 1–168 h, campioni 6–2016, α in (0, 1]',
      anomalySummary: '{hours} h · {samples} campioni · α {alpha}',
      anomalyAlphaAuto: 'auto'
    },
    nfsEngine: {
      title: 'Motore NFS (gateway Ceph)',
      hint: "A livello di cluster, replicato con le impostazioni: ogni gateway Ceph serve NFS con lo stesso motore. ganesha è NFS-Ganesha FSAL_CEPH (il default); knfsd è il server NFS del kernel che ri-esporta il mount kernel CephFS del gateway (radice NFSv4 fsid=0, un bind per share, mount con wsync). Il cambio è un atto pianificato: ogni gateway ferma un motore e accende l'altro, le sessioni NFS cadono e si riconnettono. Le share MultiSCSI restano su Ganesha FSAL_VFS comunque.",
      engine: 'Motore',
      ganesha: 'Ganesha (FSAL_CEPH)',
      knfsd: 'NFS del kernel (knfsd)',
      configure: 'Configura',
      configureTitle: 'Configura il motore NFS',
      useDefault: 'Usa il default di piattaforma (Ganesha)',
      rdma: 'NFS su RDMA (knfsd)',
      rdmaPort: 'Porta RDMA',
      rdmaOn: 'RDMA voluto, porta',
      rdmaOff: 'solo TCP',
      rdmaHint:
        'Volere di cluster; per nodo vince la realtà: un gateway ascolta in RDMA solo se ha una porta RDMA in stato ACTIVE (rdma link show) e il modulo svcrdma, altrimenti serve TCP e lo dice. La verità è il chip RDMA sulla riga del demone nfs della share (letto da /proc/fs/nfsd/portlist). I client montano con -o proto=rdma,port=<porta>. Sotto Ganesha non succede nulla.',
      saveWarning:
        'Cambiare motore riavvia NFS su ogni gateway Ceph: i mount hard si fermano e riprendono, i mount soft e i lock aperti falliscono nel cambio. Gli export Kerberos (krb5*) in questo giro li serve solo Ganesha — sotto knfsd non vengono esportati e il gateway lo dice.'
    },

    // ENEA-A-R16 «drain» (2026-10-24): il rolling update orchestrato — UN task
    // padre avanzato dallo sweep del backend, persistito e ripreso dopo un
    // riavvio del backend; si ferma al primo cancello rosso.
    rollingUpdate: {
      title: 'Rolling update',
      hint: 'Nodo per nodo: cancelli (salute, PG puliti, link di replica, indirizzi a casa, store) → drain → attesa che il nodo torni (riavviato, e sulla versione attesa se data) → undrain → cancelli → nodo successivo. Il rolling update si ferma al primo cancello rosso e non prosegue da solo. Il passo di aggiornamento è dell’operatore o della ISO: il prodotto non aggiorna se stesso.',
      operatorStep:
        'Il passo di aggiornamento è dell’operatore o della ISO: riavvia o reinstalla il nodo drenato, l’orchestrazione attende che torni.',
      none: 'Nessun rolling update in corso.',
      start: 'Avvia rolling update',
      startTitle: 'Avviare un rolling update di {name}?',
      startScale:
        'Ogni nodo selezionato sarà drenato a turno (non distruttivo: solo ruoli e indirizzi), poi l’orchestrazione attende che torni prima di riportarlo in servizio e passare oltre. Si ferma al primo cancello rosso e non prosegue da solo.',
      nodes: 'Nodi (in ordine)',
      nodesHint: 'I nodi membri in ordine di nome — togli la spunta a quelli da lasciare fuori',
      expectedVersion: 'Versione attesa (facoltativa)',
      expectedVersionHint:
        'Se data, un nodo conta come tornato solo su questa versione; altrimenti basta un riavvio del worker dopo il drain',
      awaitMinutes: 'Attesa del ritorno (minuti)',
      drainMinutes: 'Timeout del drain (minuti)',
      minutesHint: '1-1440',
      confirmLabel: 'Scrivi il nome del cluster per confermare',
      confirmHint: 'Esattamente {name}',
      startConfirm: 'Avvia',
      started: 'Rolling update avviato — task {task}.',
      kind: 'Stato',
      kinds: {
        none: 'Nessuno',
        running: 'In corso',
        paused: 'In pausa',
        stopped: 'Fermo',
        finished: 'Concluso'
      },
      progress: '{done} / {total} nodi fatti',
      current: 'Nodo corrente',
      plan: 'Piano',
      node: 'Nodo',
      state: 'Passo',
      since: 'Da',
      detail: 'Dettaglio',
      states: {
        pending: 'In attesa',
        gating: 'Cancelli',
        draining: 'Drain',
        awaitingReturn: 'Attesa del ritorno',
        undraining: 'Undrain',
        done: 'Fatto',
        failed: 'Fallito',
        skipped: 'Saltato'
      },
      stoppedAt: 'Fermo a',
      awaitingHint:
        'In attesa che il nodo torni: riavvialo o aggiornalo adesso — l’orchestrazione riprende da sola quando il worker riparte (e riporta la versione attesa, se data).',
      expectedVersionNone: 'qualunque (basta un riavvio)',
      task: 'Task',
      taskLink: 'Apri il task padre',
      pause: 'Pausa',
      resume: 'Riprendi',
      abort: 'Annulla',
      pauseTitle: 'Mettere in pausa il rolling update?',
      pauseHint:
        'Il passo in volo finisce (un drain non si interrompe a metà); nessun passo nuovo si apre finché non riprendi.',
      resumeTitle: 'Riprendere il rolling update?',
      resumeHint: 'Si torna ai cancelli del nodo corrente — anche dopo un cancello rosso, una volta sistemato.',
      abortTitle: 'Annullare il rolling update?',
      abortScale:
        'Il task padre viene annullato. Il nodo corrente resta nello stato in cui è: se è drenato, riportalo in servizio con Undrain a mano.',
      abortConfirm: 'Annulla',
      refusals: {
        nodes: 'Seleziona almeno un nodo',
        awaitMinutes: 'Minuti interi, 1-1440',
        drainMinutes: 'Minuti interi, 1-1440',
        confirm: 'Scrivi esattamente il nome del cluster'
      }
    },

    gatewayFailover: {
      title: 'Failover degli indirizzi dei gateway',
      hint: "Come tornano a casa gli indirizzi flottanti dei gateway file Ceph (CephCluster → indirizzi dei gateway) dopo un failover. Il subentro all'indirizzo di un gateway morto non è mai opzionale; il ritorno a casa è un'interruzione pianificata e breve: automatic riporta l'indirizzo quando il nodo di casa è sano da il tempo indicato (mai durante una grazia NFS né con un altro lease stantio), manual lo lascia dove il subentro l'ha messo finché l'operatore non ri-dichiara gli indirizzi del cluster.",
      rebalance: 'Ritorno a casa',
      automatic: 'automatico',
      manual: 'manuale',
      rebalanceAfterSec: 'Dopo che il nodo di casa è sano da (s)',
      configure: 'Configura',
      configureTitle: 'Configura il failover degli indirizzi dei gateway',
      useDefault: 'Usa il default di piattaforma (automatico, 60 s)',
      saveWarning:
        "Un ritorno a casa sposta un indirizzo fra gateway vivi: i mount NFS hard si fermano e riprendono, lo stato NFSv4 aperto lo ristabilisce il client, le sessioni SMB su quell'indirizzo cadono."
    },
    transportSecurity: {
      title: 'Sicurezza del trasporto',
      hint: 'Profilo a livello di cluster, replicato con le impostazioni: con plainHttp = refused nessuna share WebDAV o FTP può essere dichiarata in chiaro (tls: none risponde 422) e un object store senza certificato non converge invece di servire HTTP in chiaro. allowed è il comportamento di oggi (rig di sviluppo).',
      plainHttp: 'HTTP / FTP in chiaro',
      allowed: 'ammesso',
      refused: 'rifiutato',
      configure: 'Configura',
      configureTitle: 'Configura la sicurezza del trasporto',
      useDefault: 'Usa il default di piattaforma (ammesso)',
      saveWarning:
        'Passare a rifiutato non smonta le share già dichiarate in chiaro: vale per le nuove dichiarazioni e le PATCH, e gli object store senza certificato smettono di convergere finché non ne hanno uno.'
    },
    audit: {
      title: 'Audit',
      hint: "Ogni mutazione API a cui questo cluster ha risposto — chi l'ha fatta e cosa ha risposto, tentativi falliti compresi: un login 401 e un rifiuto 403 sono esiti, non omissioni. Le letture non vengono mai registrate, i corpi delle richieste mai conservati. Il registro è di sola lettura per costruzione e vive sullo store del cluster: con lo store giù questa lista risponde un 503 parlante di proposito — una pagina vuota dalla replica somiglierebbe a un registro pulito. I filtri corrono lato server.",
      truncated:
        'Esiste altro storico oltre queste righe (più recenti per prime) — carica le pagine più vecchie, o restringi coi filtri.',
      loadMore: 'Carica eventi più vecchi',
      occurredAt: 'Quando',
      who: 'Chi',
      action: 'Azione',
      route: 'Rotta',
      noRoute: 'Nessuna rotta corrispondente (un percorso che non esiste)',
      status: 'Stato',
      node: 'Risposto dal nodo',
      window: 'Finestra temporale',
      windows: {
        all: 'Tutto lo storico',
        '1h': 'Ultima ora',
        '24h': 'Ultime 24 ore',
        '7d': 'Ultimi 7 giorni'
      },
      method: 'Metodo',
      methodAll: 'Tutti i metodi',
      outcome: 'Esito',
      outcomes: {
        all: 'Tutti gli esiti',
        ok: 'Riusciti (2xx/3xx)',
        denied: 'Rifiutati (4xx)',
        failed: 'Errori del server (5xx)'
      },
      username: 'Nome utente',
      path: 'Percorso contiene',
      applyHint: 'Invio applica',
      eventId: 'Id evento',
      realm: 'Realm',
      sessionId: 'Sessione',
      userId: 'Id utente',
      remoteAddr: 'Indirizzo del chiamante',
      unauthenticated: 'Tentativo non autenticato',
      kind: 'Tipo',
      kindAll: 'Tutti i tipi',
      kinds: {
        mutation: 'Mutazione',
        login: 'Login',
        sensitiveRead: 'Lettura sensibile',
        prune: 'Potatura di retention'
      },
      loginAttempt: 'tentativo di login',
      loginOutcome: 'esito',
      userAgent: 'User agent',
      detail: 'Dettaglio',
      hash: 'Hash della catena',
      verify: {
        action: 'Verifica la catena',
        hint: "Ripercorre sullo store la catena di hash per nodo: l'hash di ogni riga è ricalcolato dai suoi campi e dall'hash della riga precedente. Controllo di MANOMISSIONE, non firma — chi scrive sullo store e nel codice potrebbe riscrivere entrambi.",
        rows: 'righe',
        chained: 'incatenate',
        unchained: 'non incatenate (anteriori alla catena)',
        ok: 'catena integra',
        broken: 'CATENA ROTTA — prima rottura alla riga',
        startsAfterPrune: 'la catena inizia dopo la potatura di retention del {date} ({rows} righe tolte)',
        failed: 'La verifica non è stata eseguita'
      }
    },
    logging: {
      title: 'Log e metriche',
      hint: 'Rotazione dei file di log del prodotto (resa come /etc/logrotate.d/trantor-tvstor su ogni nodo — la applica il timer logrotate di sistema) e limiti di journald (drop-in; vale dal prossimo riavvio di journald, che il worker non esegue). Il token di scrape in sola lettura apre GET /metrics (OpenMetrics) a Prometheus; anche una sessione con monitoring.read può leggerlo.',
      rotation: 'File di log',
      journal: 'Giornale (journald)',
      maxSizeMiB: 'Ruota a (MiB)',
      maxAgeDays: 'Conserva al più (giorni)',
      keep: 'Copie ruotate',
      compress: 'Comprimi i file ruotati',
      journalMaxUseMiB: 'Tetto del giornale (MiB)',
      journalMaxRetentionDays: 'Retention del giornale (giorni)',
      auditFiles: 'File dell’audit client',
      auditMaxSizeMiB: 'Audit: ruota a (MiB)',
      auditKeep: 'Audit: copie ruotate',
      auditHint:
        'Gli eventi dell’audit client (schede Audit di share e store) hanno il loro tetto: 256 MiB × 8 copie di default, mai il disco di sistema. Con l’audit acceso su share molto trafficate, il collettore syslog è il posto dove il flusso vive; il file locale è una coda.',
      configure: 'Configura',
      configureTitle: 'Configura la rotazione dei log',
      saveNote:
        'Applicata dal worker di ogni nodo al passaggio successivo. I limiti di journald valgono dal prossimo riavvio di journald.',
      boundsHint:
        'Dimensione 1–10240 MiB, età 1–3650 giorni, copie 1–365; giornale 16–1048576 MiB, retention 1–3650 giorni; file di audit 16–4096 MiB, copie 1–64',
      metricsTitle: 'Token di scrape OpenMetrics',
      tokenConfigured: 'Un token di scrape è configurato',
      tokenMissing: 'Nessun token di scrape — GET /metrics risponde solo a una sessione con monitoring.read',
      issuedAt: 'coniato {time}',
      issue: 'Conia il token',
      rotate: 'Ruota il token',
      revoke: 'Revoca',
      issueTitle: 'Conia il token di scrape',
      issueWarning:
        'Il token si vede UNA volta, qui. Coniarne uno nuovo invalida subito il precedente — ogni Prometheus che lo usa smette di leggere finché non viene aggiornato.',
      tokenOnce: 'Copialo adesso — la console non lo mostra più (si conserva solo il suo sha256).',
      snippet: 'scrape_config di Prometheus',
      snippetHint:
        "Il target è l'origine di questa console (che inoltra /metrics all'API); anche l'origine dell'API risponde allo stesso scrape. Il token viaggia come Bearer — solo HTTPS.",
      revokeTitle: 'Revocare il token di scrape?',
      revokeWarning:
        'Le letture con bearer rispondono 401 da subito; una sessione con monitoring.read continua a leggere.',
      revoked: 'Token di scrape revocato'
    },

    // WEBEXP-EDIT #63 (2026-08-05, ratifica Luca — Collabora/CODE via WOPI):
    // il server editor esterno, leaf di settings SENZA default — null =
    // editing office spento, il mint dell'explorer risponde un 409 parlante.
    documentEditor: {
      title: 'Editor di documenti',
      hint: "Il server Collabora Online (CODE) esterno su cui viaggia l'editing office del file explorer via WOPI. Non configurato significa editing office spento — l'explorer lo dice invece di mostrare un editor rotto. Il server Collabora deve fidarsi del materiale TLS del cluster (monta la CA del cluster nel suo trust store); il container gestito dalla piattaforma è un follow-up dichiarato.",
      serverUrl: 'URL del server Collabora',
      serverUrlHint:
        "URL base http(s) del server Collabora Online (es. https://code.example.com) — il server DEVE girare con net.service_root=/api/v1/collabora (v. manuale operatore); il backend ne legge la discovery sotto quel prefisso per comporre i link dell'editor",
      notConfigured: "Non configurato — l'editing office nel file explorer è spento",
      configure: 'Configura',
      configureTitle: "Configura l'editor di documenti",
      enabled: 'Editor di documenti configurato',
      enabledHint: "Deselezionato rimuove la configurazione — l'editing office nel file explorer si spegne",
      saveWarning:
        'Il server indicato qui riceve editor token a vita breve e i byte di ogni file aperto in modifica — puntalo solo a un server Collabora di cui ti fidi.',
      certFingerprint: 'Impronta del certificato del server (àncora TLS)',
      certFingerprintHint:
        'sha-256 del certificato servito dal server Collabora, 64 caratteri esadecimali (i due punti sono tollerati). Obbligatoria per un server https fuori loopback — un server di loopback è ancorato dal suo indirizzo, uno http semplice non ha TLS da ancorare.',
      pinRequired:
        "Un server Collabora https fuori loopback non viene mai contattato senza l'impronta del suo certificato — il mint dell'editor e il proxy rifiuterebbero nominando questo campo.",
      wopiBaseUrl: 'URL base di callback WOPI',
      wopiBaseUrlHint:
        "L'URL base con cui il SERVER COLLABORA richiama questo cluster ({base}/api/v1/wopi/files/…). Vuoto = https://127.0.0.1:<porta>, il server co-locato del rig e dell'appliance — mai lo Host del browser, che il container dell'editor tipicamente non raggiunge. Impostalo quando il server Collabora gira fuori da questo nodo.",
      wopiBaseUrlDefault:
        'Default — https://127.0.0.1:<porta di ascolto> (server co-locato); imposta un URL quando Collabora gira fuori da questo nodo'
    },

    // WIT-2 (2026-08-05, dettatura 2026-08-04 — 10° giro + addenda): la
    // modalità di failover a livello cluster a 3+ nodi — quorum vs fencing.
    failover: {
      title: 'Modalità di failover',
      hint: 'A 3+ nodi il failover automatico diventa una scelta a livello di cluster. Quorum (default a 3+, obbligata con ceph): la maggioranza dello store di cluster arbitra ogni authority — il fencing attivo è disarmato, le credenziali restano dormienti, e sotto la maggioranza il cluster si ferma. Fencing (opt-in, solo cluster senza ceph): la dottrina fence-and-exclude del 2-nodi generalizzata — sopravvive alla caduta di tutti i nodi meno uno, al prezzo dell’infrastruttura di fencing verificata su ogni nodo. A 2 nodi la modalità effettiva è sempre fencing: il modello 2-nodi resta intatto.',
      mode: 'Modalità dichiarata',
      effectiveMode: 'Modalità effettiva',
      modes: {
        auto: 'Automatica (per numero di nodi)',
        quorum: 'Quorum',
        fencing: 'Fencing'
      },
      modeHints: {
        auto: 'Risolve per solo numero di nodi: fencing a 2 membri, quorum a 3+. Il join del 3º nodo disarma il fencing; il ritorno a 2 lo riarma.',
        quorum:
          'Arbitraggio a maggioranza dello store a 3+ nodi. Su un cluster a 2 nodi resta dormiente — non c’è maggioranza con cui arbitrare.',
        fencing:
          'Fence-and-exclude a catena fino all’ultimo nodo in piedi. Rifiutata finché esiste un datapool ceph, e solo con le credenziali di fencing di ogni membro verificate.'
      },
      witness: 'Ruolo witness',
      witnessNone:
        "Nessuno dichiarato — arbitra la maggioranza dello store senza voto witness. Il detentore è una dichiarazione dell'operatore (Configura), mai assegnato dalla piattaforma.",
      degradedFallback:
        "La configurazione di failover nomina qualcosa che non c'è più — il detentore witness dichiarato non è più un membro; sposta il ruolo a mano.",
      witnessRole: 'witness',
      survivalLevel: 'Promessa di sopravvivenza',
      survivalLevels: {
        'n-1':
          'N-1 — sopravvive alla perdita di un membro (la promessa onesta senza credenziali di fencing verificate)',
        'n-2': 'N-2 e oltre — fence-and-exclude a catena, credenziali di fencing verificate su ogni membro'
      },
      witnessSelect: 'Detentore witness',
      witnessNoneOption: 'Nessuno — niente voto witness',
      witnessSelectHint:
        "Una DICHIARAZIONE dell'operatore su un membro, per nome — mai assegnata dalla piattaforma. Nominare un non-membro è rifiutato coi membri elencati; se il detentore dichiarato poi esce, il cluster si rende degradato con la riparazione nominata.",
      fencingState: 'Stato fencing per nodo',
      armed: 'fencing armato',
      dormant: 'fencing dormiente',
      notMember: 'non ancora membro',
      configure: 'Configura',
      configureTitle: 'Configura il failover',
      saveWarning:
        'La modalità di failover decide COME un’authority possa mai essere presa quando un nodo muore. L’opt-in del fencing è rifiutato finché esiste un datapool ceph e pretende credenziali di fencing verificate su ogni nodo; quorum e automatica sono sempre sicure da impostare.'
    },

    // ENC-1 (2026-08-05, ratifica 2026-08-04 §1): il tunnel WireGuard
    // gestito dalla piattaforma sulla rete di replica — la card impostazioni.
    replicaEncryption: {
      title: 'Cifratura della rete di replica',
      hint: "Un tunnel WireGuard gestito dalla piattaforma tra i nodi sulla rete di replica. L'attivazione conia una coppia di chiavi per nodo (keystore, metà privata write-only) e migra il trasporto NVMe/TCP interno sull'overlay 169.254.77.0/24, una gamba mirror alla volta e solo con handshake freschi. La porta UDP WireGuard deve essere aperta tra i nodi. Replica dello store e ship DR restano sul filo nudo in questo round (migrano col round del modello di rete).",
      enabled: 'Attiva',
      disabled: 'Disattiva',
      enabledLabel: 'Cifra la rete di replica (tunnel WireGuard)',
      listenPort: 'Porta UDP di ascolto',
      listenPortHint: '1-65535, default 51820 — la stessa porta su ogni nodo; va aperta tra i nodi',
      mtu: 'MTU del tunnel',
      mtuHint: '1280-9216; vuoto = default del kernel (1420). Da impostare con reti di replica jumbo-frame',
      mtuDefault: 'default del kernel',
      keepalive: 'Keepalive (s)',
      keepaliveHint: "0-3600, default 25; 0 disabilita — l'età dell'handshake è il segnale di salute del tunnel",
      nodeColumn: 'Nodo',
      overlayAddress: 'Indirizzo overlay',
      publicKey: 'Chiave pubblica',
      configure: 'Configura',
      configureTitle: 'Configura la cifratura della rete di replica',
      saveWarning:
        "Questa modifica tocca il PIANO DATI: l'attivazione migra il trasporto delle gambe mirror sul tunnel (una gamba alla volta, con gate sull'handshake); il re-bind dell'ascolto fa cadere una volta le gambe in ingresso di ogni nodo — meglio una finestra tranquilla. La disattivazione torna al filo non cifrato."
    },
    tabs: {
      nodes: 'Nodi',
      witness: 'Witness',
      store: 'Store',
      networks: 'Reti',
      audit: 'Audit',
      settings: 'Impostazioni'
    },
    networks: {
      title: 'Reti',
      hint: 'Reti dichiarate in cinque categorie: management (GUI/API), replicaInternal (gambe mirror ed endpoint del tunnel WireGuard), client (portali iSCSI, endpoint S3, e la casa di validazione degli indirizzi di servizio NAS e delle subnet DHCP), replicaExternal (DR tra cluster), san (indirizzo sorgente dei login iSCSI/NVMe-oF dei vdisk external verso SAN terze). Dichiarare una rete è ciò che migra una superficie fuori dai seam a indirizzo unico; senza reti dichiarate tutto continua a funzionare come prima. I nodi si legano uno per uno — ogni mutazione richiede sudo: un binding sbagliato taglia il piano dati.',
      name: 'Nome',
      nameHint: 'Lettere minuscole, cifre e trattini',
      category: 'Categoria',
      categoryHint: 'Immutabile dopo la creazione — al più una rete replicaInternal e una replicaExternal per cluster',
      categories: {
        management: 'Management',
        replicaInternal: 'Replica (interna)',
        client: 'Client',
        replicaExternal: 'Replica (esterna / DR)',
        san: 'SAN (LUN esterne)'
      },
      vlanId: 'VLAN',
      vlanHint: 'Tag 802.1Q 1-4094; vuoto = non taggata',
      untagged: 'non taggata',
      subnet: 'Subnet',
      subnetHint:
        'CIDR, IPv4 o IPv6 (es. 10.20.0.0/24, fd00:20::/64) — gli indirizzi dei binding devono cadervi dentro; modificabile solo senza binding',
      mtu: 'MTU',
      mtuHint:
        '576-65520 (9216 sui carrier Ethernet, 65520 su IPoIB connected mode — controllato sul carrier osservato al binding); vuoto = MTU delle interfacce lasciata intatta',
      mtuUntouched: 'intatta',
      editTitle: 'Modifica la rete {name}',
      editBlipWarning:
        'Un cambio di VLAN o MTU converge via reconcile su OGNI nodo con binding — un sussulto del piano dati su quella rete. La categoria è immutabile.',
      subnetBoundHint:
        'Modificabile solo finché la rete non ha binding — rinumerare sotto indirizzi vivi è cancella-e-ridichiara',
      vlanEditHint: '1-4094; vuoto = torna untagged',
      mtuEditHint: '576-65520 (9216 su Ethernet, 65520 su IPoIB); vuoto = lascia le MTU delle interfacce intatte',
      phase: 'Salute del link',
      phases: {
        ready: 'Pronta',
        degraded: 'Degradata',
        unknown: 'Sconosciuta'
      },
      bindings: 'Binding',
      bindingsEmpty: 'Nessun nodo legato — niente viaggia su questa rete finché non atterra un binding',
      createTitle: 'Dichiara rete',
      bindAction: 'Lega un nodo',
      bindTitle: 'Lega un nodo a {name}',
      bindWarning:
        "Questo È l'interruttore del piano dati: un binding replicaInternal riporta le gambe mirror del nodo (e l'endpoint WireGuard) sull'indirizzo, un binding client cambia il portale iSCSI del nodo al prossimo reconcile. Sostituire un binding è disruptive su replicaInternal.",
      node: 'Nodo',
      carrier: 'Carrier',
      carrierInterface: 'Interfaccia semplice',
      carrierBond: 'Bond gestito dalla piattaforma',
      interface: 'Interfaccia',
      interfaceHint:
        'Nome netdev del kernel (eth1, ens2f0) — non verificato in admission: un device mancante emerge come binding degradato',
      bondMembers: 'Membri del bond',
      bondMembersHint:
        'Interfacce slave separate da virgola, almeno due (eth1,eth2) — il device bond lo conia la piattaforma',
      bondMode: 'Modalità bond',
      bondModes: {
        activeBackup: 'active-backup (qualunque switch)',
        lacp: '802.3ad LACP (serve un LAG lato switch)'
      },
      lacpRate: 'LACP rate',
      lacpRates: {
        default: 'default del kernel (slow)',
        slow: 'slow (30 s)',
        fast: 'fast (1 s)'
      },
      address: 'Indirizzo',
      addressHint:
        'IP nudo dentro la subnet, senza prefisso — unico su tutta la flotta (altri binding e indirizzi di servizio delle share lo rifiutano)',
      observedLink: 'Link osservato',
      notObserved: 'Non ancora osservato',
      carrierUp: 'carrier su',
      carrierDown: 'carrier giù',
      member: 'membro',
      lacpPartner: 'Partner LACP',

      // ENEA-A-R16 «InfiniBand» (2026-10-24): i chip del censimento IPoIB e le
      // regole di binding su un carrier IB (il server è l'arbitro).
      ib: {
        transport: 'Trasporto',
        transports: {
          ethernet: 'Ethernet',
          infiniband: 'InfiniBand'
        },
        guid: 'GUID',
        pkey: 'P_Key',
        rdma: 'Porta RDMA',
        rate: 'Velocità',
        lid: 'LID',
        rulesTitle: 'Su un carrier InfiniBand (IPoIB)',
        rules: {
          noVlan:
            'Niente VLAN: 802.1Q non esiste su IPoIB — il partizionamento è il P_Key (dichiarato, non gestito in v1): dichiara come carrier l’interfaccia P_Key (ib0.8001).',
          bondActiveBackup:
            'Bond solo active-backup: il kernel non fa LACP su InfiniBand; i bond misti IB/Ethernet sono rifiutati.',
          mtuCeilings:
            'MTU fino a 65520 (connected mode) su InfiniBand, 9216 su Ethernet — la regola si applica sul carrier osservato.'
        },
        declared: 'Provato sul finto nei formati veri; la misura sulla fabric è di R18.'
      },
      unbindAction: 'Rimuovi un binding',
      unbindTitle: 'Slega un nodo da {name}',
      unbindWarning:
        "L'uscita esplicita per nodo: la piattaforma smonta ciò che ha reso (indirizzo, device VLAN, bond coniato — mai un'interfaccia fisica dichiarata) e il traffico del nodo per questa categoria ricade sui seam. Disruptive su replicaInternal.",
      noBoundNodes: 'Nessun nodo legato',
      delete: {
        title: 'Elimina la rete {name}',
        warning:
          'Si elimina solo una rete senza binding — prima vanno rimossi tutti (ogni rimozione è il ritorno esplicito del nodo sui seam). Una rete client rifiuta anche finché un indirizzo di servizio di una share o una subnet DHCP le cade dentro.'
      },
      sudoRequired: 'Ogni mutazione di rete richiede sudo — cambia (o prepara a cambiare) il piano dati'
    },

    // #189 GUI-RESIDUI (2026-08-19): i campi di piano-cluster che GET /cluster
    // portava da sempre e nessuna card rendeva — quorum, ancore di
    // convergenza, epoch, fallback stale.
    plane: {
      quorum: 'Quorum dello store',
      quorumStates: {
        single: 'Membro singolo',
        replicated: 'Replicato'
      },
      quorumHints: {
        single: 'Un solo membro dello store — il cluster-di-uno: niente replica, niente ack set.',
        replicated: "Un primario più N secondari in streaming, ack sull'intero ack set (strict-all)."
      },
      configGeneration: 'Generazione config',
      configGenerationHint:
        "La generazione CORRENTE della configurazione — l'ancora della vista di convergenza. Dallo store quando raggiungibile; nel fallback stale è la generazione applicata di questo nodo.",
      minReplicatedGeneration: 'Pavimento della flotta',
      minReplicatedGenerationHint:
        'Il minimo dei watermark di generazione dei membri — fin dove è converso il nodo più lento. Trattino quando lo store è irraggiungibile o un membro non ha riportato.',
      epoch: 'Epoch',
      epochHint:
        "Lo uuid di incarnazione dello store — coniato alla formazione, RI-CONIATO da ogni promozione e restore d'emergenza (il segnale di fencing della barriera di rejoin). I nodi si àncorano all'epoch a cui appartengono.",
      staleBanner:
        'RENDER STALE: lo store del cluster non ha risposto, quindi questi campi arrivano dalla replica LOCALE di questo nodo — la verità più fresca che possiede, non necessariamente quella del cluster.'
    },

    // #189 GUI-RESIDUI: il blocco security — IN SOLA LETTURA DA CONTRACT. Il
    // round Settings è arrivato (#196 SETTINGS-1, 2026-08-21) e NON ha potuto
    // aprirlo: PATCH /cluster risponde ancora 422 immutable_field su
    // `security` — dichiarato, domanda in docs/settings1-2026-08-21.md.
    security: {
      title: 'Policy di sicurezza',
      hint: "La policy di sicurezza dell'intero cluster: memorizzata nei settings replicati e VALUTATA da ogni nodo sulla sua replica locale — un'interruzione non sospende mai l'applicazione.",
      passwordMinLength: 'Lunghezza minima password',
      sessionMaxIdle: 'Inattività massima di sessione',
      lockoutThreshold: 'Blocco dopo',
      lockoutDuration: 'Durata del blocco',
      characters: '{count} caratteri',
      minutes: '{count} minuti',
      attempts: '{count} tentativi falliti',
      readOnlyNote:
        'In sola lettura da contract: PATCH /cluster rifiuta il blocco security (422 immutable_field — «la scrittura della security policy arriva col suo milestone»). Il round Settings (2026-08-21) ha aperto le manopole MFA nel tab Users → MFA e ha lasciato questo blocco recintato: la sua scrittura è un milestone di contract a sé, dichiarato — non una lacuna della console.'
    },

    // #189 GUI-RESIDUI: le note sul SINGLETON — sudo, come ogni scrittura di
    // questa superficie (una sola scala di pericolo per il singleton,
    // 2026-08-04).
    notesCard: {
      title: 'Note del cluster',
      editTitle: 'Modifica le note del cluster',
      editHint: "Testo libero dell'operatore sul cluster stesso — reso come Markdown",
      sudoHint: 'La PATCH del singleton è protetta da sudo per intero — una sola scala di pericolo, anche per le note.'
    },

    store: {
      title: 'Store del cluster',
      hint: "Lo store del cluster è PostgreSQL gestito da TVStor: un primario più N secondari in streaming replication. Una scrittura di config è confermata solo quando OGNI membro dell'insieme di ack l'ha ackata (strict-all, ratifica 9° giro); un secondario caduto viene fencato ed escluso prima che le scritture riprendano, e rientra nell'insieme solo da allineato. La promozione la decide la piattaforma (grant del witness o fencing verificato), mai il database.",
      unavailable:
        'Il blocco store non è disponibile — o lo store è irraggiungibile, o ti manca il permesso cluster.store.read.',
      degraded: 'Store degradato',
      degradedNoReason:
        "La durabilità dello store è sotto la membership dichiarata. Guarda l'insieme di ack — i membri fencati sono esclusi, quelli in sync non ancora ammessi.",
      degradedReasons: {
        'ack-blocked':
          "Un membro dell'insieme di ack ha smesso di ackare — i commit di config sono BLOCCATI finché il suo fencing non è verificato e il membro escluso (fence-before-degrade).",
        'fenced-excluded':
          "Un secondario è stato fencato ed escluso dall'insieme di ack. Le scritture continuano — la durabilità è ridotta finché non si riallinea e rientra.",
        'no-secondary':
          "Nessun secondario è nell'insieme di ack — le scritture di config sono durabili solo in locale.",
        'no-primary': 'Nessun membro dello store dichiara il ruolo di primario — lo store è senza testa.',
        'promotion-in-flight':
          "È in corso una promozione dello store — l'insieme di ack si sta ricostruendo attorno al nuovo primario."
      },

      primary: 'Primario',
      primaryUnknown: 'Sconosciuto — il render dello store è stantio',
      lastPromotedAt: 'promosso il {time}',
      lastPromotedAtUnknown:
        'data di promozione non più nota (riga precedente allo stamp — si ripara alla prossima promozione)',
      ackSet: 'Insieme di ack',
      ackSetHint:
        'Ogni membro elencato qui deve ackare ogni commit di config (strict-all, ratifica 9° giro). Un membro caduto viene fencato ed escluso; uno nuovo o di ritorno entra solo da allineato.',
      ackSetEmpty: 'vuoto — nessuna durabilità sincrona',
      ack: 'Ack',
      ackStates: {
        in: "Nell'insieme",
        fenced: 'Fencato',
        syncing: 'In sync'
      },
      ackStateHints: {
        in: "Nell'insieme di durabilità — ogni commit di config aspetta questo membro",
        fenced:
          "Fencato con verifica ed ESCLUSO dall'insieme di ack — bersaglio di resync, mai promosso automaticamente (solo la promozione con conferma digitata può)",
        syncing: "Fuori dall'insieme di ack finché non si allinea — un basebackup fresco o un membro fencato di ritorno"
      },
      standbyNames: 'synchronous_standby_names',
      standbyNamesHint:
        "Quello che la passata di reconcile ha APPLICATO sul primario — atteso dall'insieme di ack corrente: {expected}",
      standbyNone: 'vuoto — nessun ack sincrono richiesto',

      node: 'Nodo',
      role: 'Ruolo',
      roles: {
        primary: 'Primario',
        secondary: 'Secondario',
        none: 'Non membro'
      },
      roleHints: {
        primary: "L'istanza scrivibile su cui si risolve l'handle dello store di tutto il cluster",
        secondary:
          "Uno standby in streaming, in recovery — mai servito come store: la probe di salute dell'handle rifiuta un candidato in recovery",
        none: 'Non è un membro dello store'
      },
      syncState: 'Stato di sync',
      syncStates: {
        sync: 'Sync',
        quorum: 'Quorum',
        potential: 'Potenziale',
        async: 'Async',
        disconnected: 'Disconnesso',
        unknown: 'Sconosciuto'
      },
      syncStateHints: {
        sync: 'Acka in modo sincrono — i commit di config aspettano questo membro (nella lista FIRST-n applicata)',
        quorum: 'Fa parte di un insieme di ack a quorum — una configurazione ANY-k estranea (TVStor rende FIRST-n)',
        potential: 'Configurato ma non attualmente promesso — una configurazione ANY-k estranea',
        async: "Streamma fuori dall'insieme di ack — non blocca mai un commit e può restare indietro",
        disconnected: 'Lo slot esiste e nessuno streamma — si sta trattenendo WAL sul primario',
        unknown: 'Non ancora osservato'
      },
      lag: 'Ritardo (WAL / replay)',
      lagHints: {
        unknown: 'Non ancora osservato',
        ok: 'Allineato — promuoverlo non perderebbe nulla di osservabile',
        warning: 'Sta restando indietro — promuoverlo potrebbe perdere scritture di config recenti',
        critical:
          "Molto indietro — promuovere questo membro perderebbe scritture di config; controlla prima il link e l'istanza"
      },
      notObserved: 'Non osservato',
      slot: 'Slot di replica',
      endpoint: 'Endpoint',
      observedAt: 'Osservato il',
      membersEmpty: 'Nessun membro dello store ancora osservato',

      addMember: 'Aggiungi membro',
      addMemberTitle: 'Aggiungi un membro allo store',
      addMemberHint:
        "Il nodo riceve uno slot di replica sul primario, un pg_basebackup e un'istanza PostgreSQL supervisionata dal worker. Entra nell'insieme di ack solo da streammante e allineato — la copia iniziale non blocca mai le scritture di config.",
      addMemberNodeHint: 'Un nodo registrato che non è ancora membro dello store',
      addMemberSubmit: 'Aggiungi membro',
      noCandidates: 'Ogni nodo registrato è già membro dello store',
      noPrimaryBlock: 'Lo store non ha un primario raggiungibile — un membro non può nascere dal nulla',

      promote: 'Promuovi',
      promoteTitle: 'Promuovi {name} a primario dello store',
      promoteWarning:
        "La promozione ri-conia l'epoch del cluster: ogni altro membro converge in giù sul nuovo primario, e il vecchio primario al ritorno si vede rifiutare il ruolo di primario e rientra da secondario. È rifiutata se un grant del witness o un fencing verificato non provano ancora che il primario attuale è fermo.",
      promoteConfirmLabel: 'Nome del nodo',
      promoteConfirmHint: 'Digita {name} per confermare la promozione',
      promoteFencedWarning:
        "Questo membro è FENCATO ed escluso dall'insieme di ack: non è garantito aggiornato, e promuoverlo PERDE scritture di config già ackate. Questa promozione con conferma digitata è l'unica via che può farlo — l'automatico la rifiuta (gemella di forcePromote).",
      promoteSubmit: 'Promuovi',
      promotePrimaryBlock: 'È già il primario',

      removeMember: 'Rimuovi',
      removeMemberTitle: 'Rimuovere {name} dallo store?',
      removeMemberWarning:
        "Prima si azzera la membership (mentre il membro acka ancora — strict-all), poi l'istanza viene fermata e il suo slot di replica viene ELIMINATO sul primario — uno slot lasciato lì trattiene WAL per sempre. Rientrare più avanti significa un basebackup nuovo.",
      removeMemberSubmit: 'Rimuovi membro',
      removalProblems: {
        'is-primary':
          'Il primario non si rimuove mai — promuovi prima un altro membro, lo store non resta mai senza testa'
      }
    }
  }
}
